import { useState, useEffect, useRef } from "react";
import BASE_QUOTES from './quotes.json';
import { supabase } from './supabase';

const EMAIL_DOMAIN = "girlaf.me";

const JOURNAL_COLS = "id, mood, entry_text, prompt_text, written_at";

function mapJournalRow(r) {
  const d = new Date(r.written_at);
  return {
    id: r.id,
    mood: r.mood ?? 3,
    text: r.entry_text,
    prompt: r.prompt_text || "",
    date: d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
    time: d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
  };
}

// ─── FONTS ───────────────────────────────────────────────────────
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=Pacifico&family=Nunito:wght@400;600;700;800;900&display=swap";
document.head.appendChild(fontLink);

// ─── THEME ───────────────────────────────────────────────────────
const LIGHT = {
  bg:"#FFF5FA", card:"#FFFFFF", border:"#FFD6EC",
  text:"#2D1B33", sub:"#9B7BB0", accent:"#FF4FA3",
  purple:"#C84BFF", gold:"#FFB830", coral:"#FF6B6B",
  tabBg:"#FFFFFF", inputBg:"#FFF0F7", shadow:"rgba(255,79,163,0.10)",
  fade:"#E8D5F0",
};
const DARK = {
  bg:"#0D0D1A", card:"#1A1A2E", border:"#3D2060",
  text:"#F5E6FF", sub:"#9B7BB0", accent:"#FF4FA3",
  purple:"#C84BFF", gold:"#FFB830", coral:"#FF6B6B",
  tabBg:"#161626", inputBg:"#1E1A2E", shadow:"rgba(200,75,255,0.15)",
  fade:"#2A1A40",
};

const THEMES = {
  pink:     { accent:"#FF4FA3", purple:"#C84BFF", gold:"#FFB830", border:"#FFD6EC", tabBorder:"#FFD6EC", inputBg:"#FFF0F7", shadow:"rgba(255,79,163,0.10)",   bg:"#FFF5FA", darkBg:"#0D0D1A" },
  midnight: { accent:"#6C63FF", purple:"#3B82F6", gold:"#60EFFF", border:"#C7D2FE", tabBorder:"#C7D2FE", inputBg:"#EEF2FF", shadow:"rgba(108,99,255,0.10)",  bg:"#F5F3FF", darkBg:"#0A0A1A" },
  sage:     { accent:"#4CAF82", purple:"#2D9B6F", gold:"#F4B942", border:"#B7DFC8", tabBorder:"#B7DFC8", inputBg:"#F0FAF4", shadow:"rgba(76,175,130,0.10)",  bg:"#F5FDF8", darkBg:"#0A1A10" },
  ember:    { accent:"#FF6B35", purple:"#FF4757", gold:"#FFB830", border:"#FFD4C2", tabBorder:"#FFD4C2", inputBg:"#FFF5F0", shadow:"rgba(255,107,53,0.10)",  bg:"#FFF8F5", darkBg:"#1A0A05" },
  lavender: { accent:"#9B7BB0", purple:"#7C5CBF", gold:"#C9A9E0", border:"#E0D4F0", tabBorder:"#E0D4F0", inputBg:"#F5F0FF", shadow:"rgba(155,123,176,0.10)", bg:"#FAF7FF", darkBg:"#0F0A1A" },
  noir:     { accent:"#AAAAAA", purple:"#777777", gold:"#CCCCCC", border:"#DDDDDD", tabBorder:"#DDDDDD", inputBg:"#F5F5F5", shadow:"rgba(0,0,0,0.10)",       bg:"#FAFAFA", darkBg:"#0A0A0A" },
};

const THEME_META = [
  { id:"pink",     emoji:"🌸", label:"Pink" },
  { id:"midnight", emoji:"🌙", label:"Midnight" },
  { id:"sage",     emoji:"🌿", label:"Sage" },
  { id:"ember",    emoji:"🔥", label:"Ember" },
  { id:"lavender", emoji:"💜", label:"Lavender" },
  { id:"noir",     emoji:"🖤", label:"Noir" },
];

// ─── MOVE DATA ───────────────────────────────────────────────────
const MOVE_LEVELS = [
  { id:1, label:"Seedling 🌱", subtitle:"Gentle & easy — just begin", mult:1.0, needed:5, sessionName:"Morning Bloom" },
  { id:2, label:"Blooming 🌸", subtitle:"Building your morning rhythm", mult:1.2, needed:10, sessionName:"Morning Bloom" },
  { id:3, label:"Growing 🌿", subtitle:"Feeling the strength emerge", mult:1.4, needed:15, sessionName:"Morning Bloom" },
  { id:4, label:"Thriving 🌺", subtitle:"Confident, consistent, capable", mult:1.7, needed:20, sessionName:"Morning Bloom" },
  { id:5, label:"Radiant 🌟", subtitle:"Full energy, full power", mult:2.0, needed:99, sessionName:"Morning Bloom" },
];

const LEVEL_EXERCISES = {
  1: [
    { name:"Child's Pose", emoji:"🧘‍♀️", base:45, cue:"Breathe into your back body", instruction:"Kneel and fold forward, arms stretched long. Pure rest." },
    { name:"Box Breathing", emoji:"🟦", base:60, cue:"In 4 → hold 4 → out 4 → hold 4", instruction:"Breathe like drawing a square. Steady and calm." },
    { name:"Belly Breath", emoji:"🫁", base:45, cue:"Slow inhale into belly → slow exhale", instruction:"Hand on belly. Breathe deep so your hand rises gently." },
    { name:"Glute Bridge", emoji:"🌉", base:30, cue:"Exhale up → inhale down", instruction:"Press through heels, lift hips. Squeeze at the top. Lower slowly." },
  ],
  2: [
    { name:"Child's Pose", emoji:"🧘‍♀️", base:45, cue:"Breathe into your back body", instruction:"Kneel and fold forward, arms stretched long. Pure rest." },
    { name:"Box Breathing", emoji:"🟦", base:60, cue:"In 4 → hold 4 → out 4 → hold 4", instruction:"Breathe like drawing a square. Steady and calm." },
    { name:"Belly Breath", emoji:"🫁", base:45, cue:"Slow inhale into belly → slow exhale", instruction:"Hand on belly. Breathe deep so your hand rises gently." },
    { name:"Glute Bridge", emoji:"🌉", base:30, cue:"Exhale up → inhale down", instruction:"Press through heels, lift hips. Squeeze at the top." },
    { name:"Cat-Cow Flow", emoji:"🐈", base:40, cue:"Inhale arch | Exhale round", instruction:"On hands and knees. Flow like water with your breath." },
    { name:"Neck Rolls", emoji:"🌙", base:30, cue:"Slow exhale each direction", instruction:"Slowly roll your neck ear to shoulder. Release tension gently." },
  ],
  3: [
    { name:"Child's Pose", emoji:"🧘‍♀️", base:45, cue:"Breathe into your back body", instruction:"Kneel and fold forward, arms stretched long." },
    { name:"Box Breathing", emoji:"🟦", base:60, cue:"In 4 → hold 4 → out 4 → hold 4", instruction:"Breathe like drawing a square. Steady and calm." },
    { name:"Cat-Cow Flow", emoji:"🐈", base:40, cue:"Inhale arch | Exhale round", instruction:"On hands and knees. Flow like water with your breath." },
    { name:"Glute Bridge", emoji:"🌉", base:35, cue:"Exhale up → inhale down", instruction:"Press through heels, lift hips. Squeeze at the top." },
    { name:"Dead Bug", emoji:"🪲", base:35, cue:"Exhale as you lower limbs", instruction:"Lower opposite arm and leg without arching back. Switch." },
    { name:"Hip Circles", emoji:"🌀", base:30, cue:"Inhale one way → exhale the other", instruction:"Feet wide, hands on hips. Big slow circles. 5 each direction." },
    { name:"Belly Breath", emoji:"🫁", base:45, cue:"Slow inhale into belly → slow exhale", instruction:"End with breath. Hand on belly. Slow and intentional." },
  ],
  4: [
    { name:"Child's Pose", emoji:"🧘‍♀️", base:45, cue:"Breathe into your back body", instruction:"Kneel and fold forward, arms stretched long." },
    { name:"4-7-8 Breath", emoji:"🌬️", base:60, cue:"Inhale 4 → hold 7 → exhale 8", instruction:"Long slow exhale calms your nervous system." },
    { name:"Cat-Cow Flow", emoji:"🐈", base:40, cue:"Inhale arch | Exhale round", instruction:"On hands and knees. Flow like water with your breath." },
    { name:"Glute Bridge", emoji:"🌉", base:40, cue:"Exhale up → inhale down", instruction:"Press through heels, lift hips. Squeeze at the top." },
    { name:"Dead Bug", emoji:"🪲", base:35, cue:"Exhale as you lower limbs", instruction:"Lower opposite arm and leg without arching back." },
    { name:"Bird Dog", emoji:"🐦", base:35, cue:"Breathe steadily throughout", instruction:"On hands and knees. Extend opposite arm and leg. Hold 2 sec." },
    { name:"Modified Plank", emoji:"💪", base:25, cue:"Slow steady breaths", instruction:"On forearms and knees. Body in one long line." },
    { name:"Hip Circles", emoji:"🌀", base:30, cue:"Inhale one way → exhale the other", instruction:"Big slow circles. 5 each direction." },
  ],
  5: [
    { name:"Child's Pose", emoji:"🧘‍♀️", base:45, cue:"Breathe into your back body", instruction:"Kneel and fold forward, arms stretched long." },
    { name:"4-7-8 Breath", emoji:"🌬️", base:60, cue:"Inhale 4 → hold 7 → exhale 8", instruction:"Long slow exhale calms your nervous system." },
    { name:"Cat-Cow Flow", emoji:"🐈", base:40, cue:"Inhale arch | Exhale round", instruction:"On hands and knees. Flow like water with your breath." },
    { name:"Glute Bridge", emoji:"🌉", base:40, cue:"Exhale up → inhale down", instruction:"Press through heels, lift hips. Squeeze and hold." },
    { name:"Dead Bug", emoji:"🪲", base:40, cue:"Exhale as you lower limbs", instruction:"Lower opposite arm and leg without arching back." },
    { name:"Bird Dog", emoji:"🐦", base:35, cue:"Breathe steadily throughout", instruction:"Extend opposite arm and leg. Hold 2 sec. Switch." },
    { name:"Modified Plank", emoji:"💪", base:30, cue:"Slow steady breaths", instruction:"On forearms and knees. Body in one long line." },
    { name:"Hip Circles", emoji:"🌀", base:30, cue:"Inhale one way → exhale the other", instruction:"Big slow circles. 5 each direction." },
    { name:"Supine Twist", emoji:"🌀", base:40, cue:"Deep slow breaths — let gravity work", instruction:"Lie on back, drop knees to one side. Hold. Switch." },
    { name:"Marching Abs", emoji:"🥁", base:30, cue:"Exhale as each foot lifts", instruction:"Lie on back, knees bent. Lift one foot 2 inches, lower, switch." },
  ],
};

const AVATARS = ["💜","🩷","💛","🩵","🌸","✨","💚","🧡","🔥","🌺"];
const MOOD_E = ["😔","😕","😐","🙂","😊"];

// ─── JOURNAL PROMPTS ─────────────────────────────────────────────
const JOURNAL_PROMPTS = [
  "What's one thing you did today that your future self will thank you for?",
  "What are you proud of right now, even if it's small?",
  "What's been weighing on you? Say it here — no judgment.",
  "What do you need more of this week?",
  "Describe a moment recently where you felt completely yourself.",
  "What would you tell your younger self today?",
  "What's one fear you're ready to stop letting run the show?",
  "Who in your life makes you feel the most alive? Why?",
];

// ─── CLAUDE PROXY ────────────────────────────────────────────────
const CLAUDE_PROXY_URL = "https://girlaf-proxy.vercel.app/api/claude";
const PROXY_SECRET = process.env.REACT_APP_PROXY_SECRET;

async function callClaude(body) {
  const res = await fetch(CLAUDE_PROXY_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-proxy-secret": PROXY_SECRET,
    },
    body: JSON.stringify(body),
  });
  return res.json();
}

// ─── MAIN APP ────────────────────────────────────────────────────
const _savedQuotes = JSON.parse(localStorage.getItem('userQuotes') || '[]');
const _initialQuotes = [...BASE_QUOTES, ..._savedQuotes];

export default function App() {
  const [dark, setDark]               = useState(false);
  const [theme, setTheme]             = useState("pink");
  const [tab, setTab]                 = useState(0);
  const [friends, setFriends]         = useState([]);
  const [journalEntries, setJournal]  = useState([]);
  const [storyProgress, setStoryProgress] = useState({}); // { [story_id]: chapter_reached }
  const [quotes, setQuotes]           = useState(_initialQuotes);
  const [quoteIdx, setQuoteIdx]       = useState(Math.floor(Math.random() * _initialQuotes.length));
  const [quoteVisible, setQuoteVis]   = useState(true);
  const [moveScreen, setMoveScreen]   = useState("home");
  const [moveLevel, setMoveLevel]     = useState(1);
  const [moveSessions, setMoveSess]   = useState(0);
  const [moveStreak, setMoveStreak]   = useState(0);
  const [exIdx, setExIdx]             = useState(0);
  const [sec, setSec]                 = useState(0);
  const [running, setRunning]         = useState(false);
  const [resting, setResting]         = useState(false);
  const [location, setLocation]       = useState(null);
  const [zipOverride, setZipOverride] = useState("");
  const [session, setSession]         = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [guest, setGuest]             = useState(false);
  const timerRef = useRef(null);

  const baseTheme = THEMES[theme];
  const C = dark ? {
    ...DARK,
    accent: baseTheme.accent,
    purple: baseTheme.purple,
    gold: baseTheme.gold,
    border: baseTheme.border,
    tabBorder: baseTheme.tabBorder,
    shadow: baseTheme.shadow,
    bg: baseTheme.darkBg,
    inputBg: baseTheme.inputBg,
  } : {
    ...LIGHT,
    accent: baseTheme.accent,
    purple: baseTheme.purple,
    gold: baseTheme.gold,
    border: baseTheme.border,
    tabBorder: baseTheme.tabBorder,
    inputBg: baseTheme.inputBg,
    shadow: baseTheme.shadow,
    bg: baseTheme.bg,
  };
  const level = MOVE_LEVELS.find(l=>l.id===moveLevel);
  const currentEx = LEVEL_EXERCISES[moveLevel]?.[exIdx];
  const exDur = Math.round((currentEx?.base||30)*level.mult);
  const REST = 8;
  const timerTotal = resting ? REST : exDur;
  const exFrac = timerTotal > 0 ? (timerTotal-sec)/timerTotal : 1;

  // Load persisted state on mount
  useEffect(()=>{
    try {
      const fr = localStorage.getItem("girlaf_friends");
      const th = localStorage.getItem("girlaf_theme");
      if (fr) setFriends(JSON.parse(fr));
      if (th && THEMES[th]) setTheme(th);
    } catch(e) {}
  },[]);

  useEffect(()=>{ try { localStorage.setItem("girlaf_friends", JSON.stringify(friends)); } catch(e) {} },[friends]);
  useEffect(()=>{ try { localStorage.setItem("girlaf_theme", theme); } catch(e) {} },[theme]);

  // Auth bootstrap
  useEffect(()=>{
    supabase.auth.getSession().then(({ data })=>{
      setSession(data.session);
      setAuthChecking(false);
    });
    const { data:{ subscription } } = supabase.auth.onAuthStateChange((_e, s)=>{
      setSession(s);
    });
    return ()=>subscription.unsubscribe();
  },[]);

  // Fetch journal entries for the signed-in user
  useEffect(()=>{
    const userId = session?.user?.id;
    if (!userId) { setJournal([]); return; }
    let cancelled = false;
    (async ()=>{
      const { data, error } = await supabase
        .from("journal_entries")
        .select(JOURNAL_COLS)
        .eq("user_id", userId)
        .or("is_deleted.is.null,is_deleted.eq.false")
        .order("written_at", { ascending: false });
      if (cancelled || error || !data) return;
      setJournal(data.map(mapJournalRow));
    })();
    return ()=>{ cancelled = true; };
  },[session?.user?.id]);

  const addJournalEntry = async ({ mood, text, prompt }) => {
    const userId = session?.user?.id;
    if (!userId) return { error: "You're not signed in." };
    const { data, error } = await supabase
      .from("journal_entries")
      .insert({ user_id: userId, mood, entry_text: text, prompt_text: prompt || null })
      .select(JOURNAL_COLS)
      .single();
    if (error) return { error: error.message };
    if (!data)  return { error: "Couldn't save — please try again." };
    setJournal(e => [mapJournalRow(data), ...e]);
    return { error: null };
  };

  // Fetch story progress for the signed-in user
  useEffect(()=>{
    const userId = session?.user?.id;
    if (!userId) { setStoryProgress({}); return; }
    let cancelled = false;
    (async ()=>{
      const { data, error } = await supabase
        .from("story_progress")
        .select("story_id, chapter_reached")
        .eq("user_id", userId);
      if (cancelled) return;
      console.log("[story_progress] fetch", { rows: data?.length ?? 0, data, error });
      if (error) { console.error("[story_progress] fetch failed:", error); return; }
      if (!data) return;
      const map = {};
      data.forEach(r => { map[r.story_id] = r.chapter_reached; });
      setStoryProgress(map);
    })();
    return ()=>{ cancelled = true; };
  },[session?.user?.id]);

  const upsertStoryProgress = async (storyId, chapter) => {
    const userId = session?.user?.id;
    if (!userId) return { error: "Not signed in" };
    const prev = storyProgress[storyId] ?? -1;
    if (chapter <= prev) return { error: null }; // no forward movement
    setStoryProgress(p => ({ ...p, [storyId]: chapter }));

    // Update-or-insert without relying on a unique constraint:
    // try UPDATE first; if no row matched, INSERT.
    const { data: updated, error: updateError } = await supabase
      .from("story_progress")
      .update({ chapter_reached: chapter })
      .eq("user_id", userId)
      .eq("story_id", storyId)
      .select();
    console.log("[story_progress] update", { storyId, chapter, updated, updateError });

    let finalError = updateError;
    if (!updateError && (!updated || updated.length === 0)) {
      const { error: insertError } = await supabase
        .from("story_progress")
        .insert({ user_id: userId, story_id: storyId, chapter_reached: chapter });
      console.log("[story_progress] insert", { storyId, chapter, insertError });
      finalError = insertError;
    }

    if (finalError) {
      setStoryProgress(p => {
        const restored = { ...p };
        if (prev < 0) delete restored[storyId]; else restored[storyId] = prev;
        return restored;
      });
      return { error: finalError.message };
    }
    return { error: null };
  };

  const signOut = async () => { await supabase.auth.signOut(); };

  // Keep proxy warm
  useEffect(()=>{
    const ping=()=>callClaude({model:"claude-haiku-4-5-20251001",max_tokens:5,messages:[{role:"user",content:"hi"}]}).catch(()=>{});
    ping();
    const interval=setInterval(ping,240000);
    return ()=>clearInterval(interval);
  },[]);

  // Keep Supabase warm (prevents project auto-pause)
  useEffect(()=>{
    const ping=()=>supabase.from("journal_entries").select("id",{head:true,count:"exact"}).limit(1).then(()=>{},()=>{});
    ping();
    const interval=setInterval(ping,240000);
    return ()=>clearInterval(interval);
  },[]);

  // Get device location on mount
  useEffect(()=>{
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setLocation({ lat:pos.coords.latitude, lng:pos.coords.longitude, source:"device" }),
        ()  => setLocation(null)
      );
    }
  },[]);

  useEffect(()=>{
    if (running && moveScreen==="session") {
      timerRef.current = setInterval(()=>{
        setSec(s=>{ if(s<=1){ advanceEx(); return 0; } return s-1; });
      },1000);
    }
    return ()=>clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[running,exIdx,resting,moveScreen]);

  function advanceEx() {
    clearInterval(timerRef.current);
    if (resting) { setResting(false); setSec(exDur); return; }
    const next = exIdx+1;
    if (next>=LEVEL_EXERCISES[moveLevel].length) {
      setRunning(false); setMoveSess(s=>s+1); setMoveStreak(s=>s+1); setMoveScreen("complete");
    } else { setResting(true); setExIdx(next); setSec(REST); }
  }

  const nextQuote = () => {
    setQuoteVis(false);
    setQuoteIdx(i => { let next; do { next = Math.floor(Math.random() * quotes.length); } while (next === i); return next; });
    setTimeout(() => setQuoteVis(true), 320);
  };

  const addQuote = (newQuote) => {
    const withFlag = {...newQuote, custom: true};
    setQuotes(q => {
      const updated = [...q, withFlag];
      localStorage.setItem('userQuotes', JSON.stringify(updated.filter(x => x.custom)));
      return updated;
    });
  };

  const deleteQuote = (idx) => {
    setQuotes(q => {
      const updated = q.filter((_, i) => i !== idx);
      localStorage.setItem('userQuotes', JSON.stringify(updated.filter(x => x.custom)));
      return updated;
    });
    setQuoteIdx(i => (i >= idx && i > 0) ? i - 1 : i);
  };

  const quote = quotes[quoteIdx % quotes.length];

  const TABS = [
    {label:"Home",    icon:"☀️"},
    {label:"Move",    icon:"🌸"},
    {label:"Explore", icon:"🎯"},
    {label:"Girls",   icon:"💬"},
    {label:".me",     icon:"🔒"},
  ];

  if (authChecking) {
    return (
      <div style={{minHeight:"100vh",background:C.bg,fontFamily:"Nunito, sans-serif",display:"flex",alignItems:"center",justifyContent:"center",color:C.sub,fontWeight:700,maxWidth:430,margin:"0 auto"}}>
        Loading…
      </div>
    );
  }
  if (!session && !guest) return <AuthScreen C={C} onGuest={()=>{ setGuest(true); setTab(2); }} />;

  return (
    <div style={{minHeight:"100vh",background:C.bg,fontFamily:"Nunito, sans-serif",display:"flex",flexDirection:"column",maxWidth:430,margin:"0 auto",position:"relative",overflow:"hidden"}}>
      {/* Top bar */}
      <div style={{background:C.card,borderBottom:`1px solid ${C.border}`,padding:"14px 20px 10px",paddingTop:"calc(14px + env(safe-area-inset-top))",flexShrink:0,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontFamily:"Pacifico, cursive",fontSize:22,background:`linear-gradient(90deg,${C.accent},${C.purple})`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>GirlAF</div>
        <div style={{color:C.sub,fontSize:12,fontWeight:700}}>{new Date().toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}</div>
      </div>

      {/* Content */}
      <div style={{flex:1,overflowY:"auto",paddingBottom:80}}>
        {tab===0 && <HomeTab C={C} quote={quote} quoteVisible={quoteVisible} onNextQuote={nextQuote} />}
        {tab===1 && <MoveTab C={C} screen={moveScreen} setScreen={setMoveScreen} level={level} levels={MOVE_LEVELS} moveLevel={moveLevel} setMoveLevel={setMoveLevel} sessions={moveSessions} streak={moveStreak} onStart={()=>{setExIdx(0);setSec(Math.round(LEVEL_EXERCISES[moveLevel][0].base * level.mult));setResting(false);setRunning(false);setMoveScreen("session");}} currentEx={currentEx} exIdx={exIdx} exFrac={exFrac} sec={sec} resting={resting} running={running} onTogglePlay={()=>setRunning(r=>!r)} onSkip={()=>{clearInterval(timerRef.current);advanceEx();}} exercises={LEVEL_EXERCISES[moveLevel]} location={location} zipOverride={zipOverride} setZipOverride={setZipOverride} setLocation={setLocation} />}
        {tab===2 && <ExploreTab C={C} onAddJournalEntry={addJournalEntry} storyProgress={storyProgress} onUpsertProgress={upsertStoryProgress} />}
        {tab===3 && <GirlsTab C={C} friends={friends} setFriends={setFriends} journalEntries={journalEntries} onAddJournalEntry={addJournalEntry} />}
        {tab===4 && <PrivacyTab C={C} dark={dark} setDark={setDark} theme={theme} setTheme={setTheme} quotes={quotes} onAddQuote={addQuote} onDeleteQuote={deleteQuote} userEmail={session?.user?.email} onSignOut={signOut} />}
      </div>

      {/* Tab bar */}
      <div style={{position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",width:"100%",maxWidth:430,background:C.tabBg,borderTop:`1px solid ${C.border}`,display:"flex",zIndex:50}}>
        {TABS.map((t,i)=>(
          <button key={i} onClick={()=>setTab(i)} style={{flex:1,background:"none",border:"none",padding:"10px 0 12px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
            <span style={{fontSize:tab===i?20:16,transition:"font-size 0.2s"}}>{t.icon}</span>
            <span style={{fontSize:9,fontWeight:800,color:tab===i?C.accent:C.sub,fontFamily:"Nunito, sans-serif"}}>{t.label}</span>
          </button>
        ))}
      </div>

    </div>
  );
}

// ─── AUTH SCREEN ─────────────────────────────────────────────────
function AuthScreen({ C, onGuest }) {
  const [mode, setMode]         = useState("signin"); // signin | signup
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [info, setInfo]         = useState("");
  const [loading, setLoading]   = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail.endsWith(`@${EMAIL_DOMAIN}`)) {
      setError(`Email must end with @${EMAIL_DOMAIN}`);
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    const { data, error } = mode === "signup"
      ? await supabase.auth.signUp({ email: cleanEmail, password })
      : await supabase.auth.signInWithPassword({ email: cleanEmail, password });
    setLoading(false);
    if (error) { setError(error.message); return; }
    if (mode === "signup" && !data.session) {
      setInfo("Check your email to confirm your account.");
    }
  };

  const inputStyle = {width:"100%",background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"14px 16px",fontSize:15,color:C.text,outline:"none",fontFamily:"Nunito, sans-serif",fontWeight:600,marginBottom:12,boxSizing:"border-box"};

  return (
    <div style={{minHeight:"100vh",background:C.bg,fontFamily:"Nunito, sans-serif",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"20px",maxWidth:430,margin:"0 auto"}}>
      <div style={{fontFamily:"Pacifico, cursive",fontSize:44,background:`linear-gradient(90deg,${C.accent},${C.purple})`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",marginBottom:8}}>GirlAF</div>
      <div style={{color:C.sub,fontSize:14,fontWeight:600,marginBottom:32,textAlign:"center"}}>
        {mode==="signup" ? "Create your account ✨" : "Welcome back ✨"}
      </div>

      <form onSubmit={submit} style={{width:"100%",maxWidth:340}}>
        <input type="email" placeholder={`you@${EMAIL_DOMAIN}`} value={email} onChange={e=>setEmail(e.target.value)} autoCapitalize="none" autoCorrect="off" autoComplete="email" style={inputStyle} />
        <input type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} autoComplete={mode==="signup"?"new-password":"current-password"} style={inputStyle} />
        {error && <div style={{color:C.coral,fontSize:13,fontWeight:700,marginBottom:12,textAlign:"center"}}>{error}</div>}
        {info && <div style={{color:C.accent,fontSize:13,fontWeight:700,marginBottom:12,textAlign:"center"}}>{info}</div>}
        <button type="submit" disabled={loading} style={{width:"100%",background:`linear-gradient(135deg,${C.accent},${C.purple})`,border:"none",color:"white",borderRadius:14,padding:"14px",fontSize:16,fontWeight:800,cursor:loading?"default":"pointer",fontFamily:"Nunito, sans-serif",opacity:loading?0.6:1,marginBottom:16}}>
          {loading ? "Just a sec…" : (mode==="signup" ? "Create account" : "Sign in")}
        </button>
      </form>

      <button onClick={()=>{ setMode(m=>m==="signup"?"signin":"signup"); setError(""); setInfo(""); }} style={{background:"none",border:"none",color:C.accent,fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>
        {mode==="signup" ? "Already have an account? Sign in" : "New here? Create an account"}
      </button>

      {onGuest && (
        <button onClick={onGuest} style={{background:"none",border:"none",color:C.sub,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"Nunito, sans-serif",marginTop:20}}>
          Explore without signing up →
        </button>
      )}
    </div>
  );
}

// ─── EXPLORE: STORY DATA ─────────────────────────────────────────
const EXPLORE_STORIES = [
{
  id: "cinderella",
  title: "Cinderella",
  era: "Brothers Grimm, 1812",
  subtitle: "Aschenputtel",
  color: "#C84BFF",
  quote: "She is clothed with strength and dignity, and she laughs without fear of the future.",
  attribution: "Proverbs 31:25",
  chapters: [
    {
      number: 1,
      title: "Ashputtel",
      story: `There was once a rich man whose wife lay sick, and when she felt her end drawing near she called her only daughter to her bedside and said: "Dear child, be good and pious, and God will always take care of you, and I will look down on you from heaven and will be with you." Then she closed her eyes and was gone.

The little girl went every day to her mother's grave and wept. Winter came and the snow covered the grave like a white cloth pulled gently over something sleeping. Spring came and took the snow away. And still she went. She was good. She was pious. She was, for a long time, simply alone with her grief and her goodness, which is the hardest kind of alone there is.

Then the father remarried.

This is where most tellings of the story rush, and they shouldn't. The stepmother was not a monster in the way of fairy tales — not at first, not where anyone could see. She was a woman who had come with two daughters of her own, daughters she loved with the fierce, partial love of a mother, and she looked at this quiet, beautiful, pious girl who went to the grave every day, and she felt something she did not examine too closely. She gave it no name. She simply found, over the days and weeks, ways to make the girl smaller.

It began with work. The girl was asked to carry water and make fires and cook and wash. This was reasonable. She was part of the household. She should contribute. But the tasks multiplied the way tasks do when someone is counting, and the stepsisters contributed nothing, and the girl said nothing, and the stepmother said nothing either, and so a life was arranged in which one person did everything and was thanked for none of it.

They took her clothes. They gave her a gray smock and wooden shoes. They emptied lentils into the ashes so she would have to pick them out grain by grain, blinking against the smoke. They called her Ashputtel — Ash-girl, Cinder-girl — because she had nothing left but the hearth to sleep in.

She slept in the ashes. She rose before dawn. She did the work.

And here the story asks you to pay attention, because this is the part that gets lost in the telling. Ashputtel did not become less in those years. She did not shrink. She fed the lentils back from the ashes one by one, which is the work of patience, and patience is not nothing. She made the fires that warmed the house that her stepmother slept comfortably in. She carried the water. She cooked the food. She learned, in the way you only learn through daily practice, how a household breathes and what it needs and when, and how to give a difficult person what they actually need rather than what they're asking for, which is one of the rarest skills in the world.

She went to the hazel tree on her mother's grave and she planted a branch there and wept over it until it grew. She talked to the tree. She talked to the birds. She kept something alive inside her that the ashes couldn't touch, something her mother had put there before she died, something that went with her everywhere and waited.

They called her Cinder-girl.

She wore the name. Not with pride — not yet. But she wore it the way you wear something you've been handed: you put it on, and you go about your day, and you decide privately and slowly that it does not mean what they meant by it.`,
      journal: "What is something you do quietly, every day, that no one really sees?"
    },
    {
      number: 2,
      title: "The Hazel Tree",
      story: `The king of that country proclaimed a festival. It would last three days. All the beautiful young women of the land were invited, so that the king's son might choose himself a bride. When the two stepsisters heard this they were in high spirits, for they were beautiful enough, and they believed — as people who have been told they are beautiful from birth often do believe — that beauty was sufficient.

They called Ashputtel to them. Comb our hair, they said. Brush our shoes. Fasten our buckles. We are going to the wedding feast at the king's palace. And she combed and she brushed and she fastened, kneeling on the floor of the room that had been hers, in the gray smock she had been given, looking up at the two of them who looked down at her without seeing her at all.

She asked — quietly, without expecting anything — if she might go too.

The stepmother laughed. You, in the ashes, in those rags, without shoes? You would go to a festival? And she threw a dish of lentils into the cinders and said: Sort those in two hours and you may come.

Ashputtel went into the garden and called to the birds she knew — the doves and turtle-doves and all the birds under the sky — and they came and helped her sort the lentils into the dish in less than an hour. She brought the dish to her stepmother.

The stepmother threw two dishes of lentils into the ashes and said: Sort those in one hour and you may come.

In less than an hour the birds had sorted them all again. Ashputtel brought them.

Her stepmother looked at her and said nothing for a moment. Then she said: It's no use. You have nothing to wear. You cannot dance. You will make us ashamed. And she turned away, and the stepsisters swept out of the house in their fine clothes, and Ashputtel stood alone.

She went to her mother's grave under the hazel tree. She said: Shake yourself, little tree, shake gold and silver down on me.

The bird in the tree threw down a dress of gold and silver, and slippers embroidered with silk and silver thread, and they fit her as though they had been made for her — because in some sense they had, in the way that things we've been building toward without knowing it always fit when they finally arrive.

She put on the dress. She went to the festival.

No one knew her. Not her father, who was there. Not her stepsisters, who had made her kneel on the floor that morning. The prince danced with her and no one else. He would not leave her side. When other men asked her to dance he said: She is my partner.

When the evening came and she wanted to go, he followed her. She ran from him and sprang into the pigeon house to hide, and he lost her. She changed back into the gray smock and was sitting in the ashes when the stepsisters returned, full of the story of the strange, beautiful girl at the ball, not knowing she was sitting right in front of them.

She went back the second night, and the third. On the third night the prince had the stairs spread with pitch. When she fled, her golden slipper stuck fast. He picked it up. It was small and dainty and entirely gold.

He said: I will marry no one whose foot does not fit this slipper.`,
      journal: "Have you ever been somewhere you belonged but no one recognized you yet?"
    },
    {
      number: 3,
      title: "The Slipper",
      story: `The prince came to the house. The stepsisters tried the slipper. The eldest went into her room and her mother stood beside her with a knife and said: Cut off the toe. When you are queen you will not need to walk. The girl cut off her toe and forced on the slipper and came out smiling. The prince lifted her onto his horse and rode away with her, and they passed by the hazel tree, and the birds in the tree called out:

Look back, look back, there's blood in the track — the shoe is too small, the true bride stays back.

He looked at the slipper and saw the blood. He turned back.

The second sister tried the slipper and her mother stood beside her with a knife and said: Cut off the heel. When you are queen you will not need to walk. The girl cut and forced and came out smiling. The prince rode away with her and the birds called out again, and he turned back again.

He asked the father: Have you no other daughter? The father said, hesitating: There is only a little Ashputtel from my first wife, but she cannot possibly be the bride. The prince said: Let her come.

She washed her hands and face and went in and curtseyed and put on the golden slipper. It fit perfectly, as though it had been made for her — because it had, in every way that matters.

The prince looked at her face and said: This is the true bride.

The stepmother and the stepsisters went pale. The prince took Ashputtel onto his horse and rode away, and as they passed the hazel tree the birds sang: Look back, look back, no blood in the track, the shoe fits well, the true bride is she.

They flew down and settled on her shoulders, one on the right and one on the left, and they stayed there.

This is where the story ends in the book. The wedding. The prince. The stepsisters punished. All of it arranged and settled.

But the story is really about the years before the ball. About the ash and the gray smock and the lentils sorted from the cinders in the cold before dawn. About the hazel tree grown up from tears. About the thing she kept alive inside her through all of it — the goodness, the piety, the patience that is not the same as passivity but is its own kind of strength, the kind that takes years to build and does not announce itself.

The slipper fit because she was always the one it was made for. She just had to live through all the rest of it first.`,
      journal: "The story ends at the beginning of something new. What do you think she carried with her into that new life?"
    }
  ]
},
{
  id: "vasilisa",
  title: "Vasilisa",
  era: "Alexander Afanasyev, 1855 — ancient Slavic folk tradition",
  subtitle: "Vasilisa the Beautiful",
  color: "#F59E8A",
  quote: "You have been assigned this mountain to show others it can be moved.",
  attribution: "Mel Robbins",
  chapters: [
    {
      number: 1,
      title: "The Doll",
      story: `In a certain kingdom, in a certain land, there lived a merchant. He had been married twelve years and had one child, a daughter, who from the cradle had been called Vasilisa the Beautiful. And she was beautiful — not in the way of paintings or ornaments, but in the way of something alive and real and glad to be in the world.

When Vasilisa was eight years old her mother knew she was dying. She called her daughter to her bed. She took from under the coverlet a small wooden doll, barely as long as a finger, and she put it in Vasilisa's hands and held them closed around it.

She said: "My darling, I am going. But I leave you my blessing, and I leave you this doll. Keep it always about you. Never show it to anyone. When trouble comes, give it something to eat and drink and tell it your grief. It will help you."

Then the mother kissed her daughter and died.

Vasilisa did not understand everything her mother had told her. She was eight years old and her mother was dead. She put the doll in her pocket and she went to the grave every day and she wept, which is what you do when you are eight years old and have lost the person who was the whole world to you.

The father mourned a long time. Then he saw a widow in the town who had two daughters of her own and seemed good-natured enough, and the house needed a woman in it, and he was lonely, and so he married her.

He was wrong about the good-natured part. It was one of those mistakes that reveals itself slowly, degree by degree, until you are somewhere you never intended to be.

The stepmother wanted everything for her own daughters — the best food, the best rooms, the best chances. Vasilisa was given all the work. She rose in the dark and fetched water from the well and lit the fires and cooked and cleaned and spun. If the work was not done exactly right she was scolded. If it was done exactly right nothing was said at all. The stepsisters were pretty but lazy and mean-spirited, and they made her life a misery in the small ways that are worse than the large ones, because you cannot point to them.

Vasilisa did not break. This is the thing the story keeps coming back to. She was given more work than she could do and yet the work was always done. She was given sorrow and she carried it without collapsing. She was beautiful and grew more beautiful in those years, which infuriated her stepmother, because beauty is a kind of answer, and her stepmother had been asking a question.

The answer was the doll.

Each time the work was too much or the stepsisters were too cruel, Vasilisa would take the little doll from her pocket and give it a morsel of bread and a sip of water from the kvas jar, and she would hold it in her hands and whisper her grief into it, all the things she could not say to any living person. And the doll's eyes would begin to glow, like the tiniest of fires. And it would speak to her, in a voice only she could hear.

It would say: Don't grieve, Vasilisa. Say your prayers and go to sleep. Morning is wiser than evening.

And somehow when she woke the work that had seemed impossible was done. The grain sorted. The garden weeded. The water carried. She did not always know how. She only knew that the doll had been with her, and in the morning things were possible that hadn't been possible at night.

She asked the doll once: what are you?

It said: Your mother's love, made into something you can hold.

She kept it close. She told no one. She grew up.`,
      journal: "Is there something you carry with you — a memory, a voice, a small habit — that helps when things are at their hardest?"
    },
    {
      number: 2,
      title: "Into the Forest",
      story: `The time came when Vasilisa was old enough to marry, and every young man in the village wanted her. They came to the house and asked the stepmother, but she turned them all away — for she could not stand the thought of Vasilisa married and happy before her own daughters were settled.

And so she devised a plan.

One autumn evening she put out every light in the house — every candle, every ember in the hearth — and told the two stepsisters to work by the light of a single splinter of wood. But even that went out. The stepmother sent her daughters to the neighbors for fire, and they came back saying there was none to be had. Then the stepmother turned to Vasilisa.

"You will go into the forest," she said, "to Baba Yaga. And you will fetch fire from her."

Everyone in that kingdom knew Baba Yaga. She was the witch of the deep wood. Her house stood in a clearing on chicken legs and turned on itself. Her fence was made of human bones. The skulls on her gateposts glowed in the dark with an awful light. She ate people. Everyone knew she ate people.

The stepsisters jeered. The stepmother smiled with her mouth.

Vasilisa took her doll from her pocket. She gave it a morsel of bread and a sip of water. She said: "They are sending me to Baba Yaga for fire, and Baba Yaga will eat me."

The doll's eyes glowed. It said: "Have no fear. Go where you are sent. But keep me close, and I will not let harm befall you."

Vasilisa went.

The forest was dark in a way that forests are not usually dark — a deep dark, a specific dark, the kind that seems to have a presence in it. She walked and the trees closed behind her. She did not know the path. She walked by instinct and by the faint warmth of the doll in her pocket, which seemed to pulse like a second heartbeat.

Then a horseman rode past her in the night — dressed all in white, on a white horse, and where he rode the darkness paled. Dawn.

She walked on. Another horseman — dressed all in red, on a red horse, and where he rode the light grew warm and golden. Day.

She walked through the day and into the next darkness. A third horseman — dressed all in black, on a black horse — galloped past, and the darkness came down completely and the trees groaned and something rustled in the undergrowth that she tried not to think about.

Then she saw it. The clearing. And in the clearing the house on its chicken legs, turning slowly, the skulls on the fence lit up with their cold fire, and the gate made of bone. And a voice like the sound of a tree splitting in frost:

"Who is there?"

Vasilisa stood still. She put her hand in her pocket. She felt the doll.

She said: "It is I — Vasilisa. My stepmother's daughters sent me to you for fire."

A terrible laugh from inside the house. "I know them," said the voice. "I know you too. We'll see. Come in."`,
      journal: "Have you ever walked toward something frightening because turning back wasn't really an option?"
    },
    {
      number: 3,
      title: "What She Earned",
      story: `Baba Yaga was ancient and enormous. Her nose curved down and her chin curved up and they nearly met in the middle. Her eyes were like two coals and her hands were like bundles of sticks. She lay stretched across her house with her head in one corner and her legs in another, and she looked at Vasilisa the way something very old and very powerful looks at something young and alive, with an attention that is not quite appetite and not quite interest and is more frightening than either.

"You'll work for me," she said. "And if you do the work well enough, I'll give you fire and send you home. And if not — you'll be supper."

She set tasks that would have broken a strong man working a week. All the corn in the storehouse to be sorted grain from grit before morning. The yard swept. Her laundry done. Her supper cooked. All of it, all in one night.

Vasilisa cooked the supper — there was food in the house, haunted food in haunted pots, but it cooked if you asked it — and brought it to the witch. Baba Yaga ate and ate, enough for ten men, and drank three bucketsful of mead, and then she stretched out and said: "See that in the morning all the work is done." And she slept.

Vasilisa sat alone in the witch's house. She took the doll from her pocket. She gave it the last of the bread she'd brought and a little of the mead. She said: "Help me." The doll's eyes glowed and it said: "Sleep, Vasilisa. Morning is wiser than evening."

She slept. And when she woke before dawn the corn was sorted and the yard was swept and the laundry was done. She had only to stoke the fire and heat the water for the witch's washing.

Baba Yaga walked all around and examined everything. She found nothing to fault. She was quiet for a moment — a long moment — and there was something in her silence that was almost disappointment, the way a very difficult teacher is almost disappointed when a student has done everything right.

"Good," she said finally. "Now ask me what you will."

Vasilisa asked about the horsemen — the white one, the red one, the black one. Baba Yaga answered: they are my servants. The white one is my Bright Day. The red one is my Red Sun. The black one is my Dark Night. She narrowed her eyes. "You're a clever girl to ask only what's outside my house. Some have asked about what's inside, and I've had to eat them. Now. Have you any more questions?"

Vasilisa thought about it. She thought about the work that was done in the night when she slept, about the doll in her pocket, about her mother. "No," she said. "You have answered enough."

Baba Yaga sat back. "And that," she said, "is wisdom. Most people don't know when to stop asking." She reached into the fence and pulled out one of the skulls. The eye-sockets blazed with light. She thrust it at Vasilisa on a stick. "There is your fire. Go home."

Vasilisa took the skull and walked back through the forest. The skull's light lit the way. When she reached the edge of the wood she thought to throw it away, for surely the house would have light by now. But the skull spoke to her: "Do not throw me away. Bring me to your stepmother."

She brought it. The stepmother and stepsisters were glad to see the fire, for all the lights they'd tried to get had gone out and they'd been three days in the dark. But the moment the skull's eyes fell on them they could not look away, and wherever they turned the skull's gaze followed, burning, burning, until by morning there was nothing left of them but ash.

Vasilisa buried the skull in the garden and locked the house and walked to the city, and found an old woman to live with, and there she was quiet for a while. She had the doll in her pocket. She had what she had been through. She had — though she wouldn't have called it this — the knowledge that she had walked into the worst thing imaginable and come back with fire.

She spun. She waited. She didn't know yet what she was waiting for, but she was not afraid of the waiting.

The doll had taught her that. Morning is wiser than evening. And morning always comes.`,
      journal: "She came back from the impossible place and then she waited quietly. What do you think she was doing, inside, while she waited?"
    }
  ]
},
{
  id: "scheherazade",
  title: "Scheherazade",
  era: "One Thousand and One Nights — 8th to 14th century",
  subtitle: "One Thousand and One Nights",
  color: "#F9A85D",
  quote: "I am not afraid of storms, for I am learning how to sail my ship.",
  attribution: "Louisa May Alcott",
  chapters: [
    {
      number: 1,
      title: "The Vizier's Daughter",
      story: `It is related — but God alone is all-knowing as to what is hidden in the invisible world — that there was once a king called Shahryar who ruled a great kingdom wisely and well for twenty years. He was just. He was loved. He had a brother he trusted and a wife he loved entirely, and he believed he understood the shape of his life.

Then he discovered a betrayal so complete that something in him broke along a fault line he hadn't known was there.

He did not heal from it. Some wounds don't heal; they scar over and change the shape of the person, and the person who emerges is not the person who went in. The king emerged with a new belief: that no woman could be trusted past dawn. He would marry a new wife each evening and have her executed at first light, before she could betray him as he had been betrayed.

This went on for three years. The kingdom grieved. The vizier whose duty it was to produce these brides ran out of girls willing to go, and then of girls he was willing to send, and stood before the king hollow-eyed and broken.

The vizier had two daughters. The elder was named Scheherazade.

She had read deeply in history and in poetry and in philosophy and in the physical sciences. She had studied the works of the poets and knew their verses by heart. She had learned what makes a person who cannot be moved begin to soften, what makes a locked door swing on its hinge, what lives in a human being beneath the wound that has sealed itself over into cruelty. She had spent her life reading because she had understood, without quite knowing it, that reading was not decoration but preparation.

She told her father she would go to the king.

He wept. He argued. He told her she would die. She said: "Father, either I will save the people of this country or I will perish. There is no third way." She was not performing courage. She had thought it through and reached the only conclusion the thinking could reach.

She asked one thing: that her younger sister Dunyazade be hidden in the bridal chamber. She told Dunyazade what to say.

She went to the king.

After the ceremony, in the dark before dawn, Dunyazade called out from her hiding place: "Sister, if you are not too tired, finish that story you told me last night, for it was so wonderful."

The king lay still. There had been no story the night before. But he thought: I will hear what she says.

Scheherazade began.

She had prepared for this moment the way a musician prepares for a concert — not with nervousness but with the specific stillness of someone who knows what they are about to do. She began a tale. She built it carefully, the way you build a fire that will last the night, with attention to what burns and what doesn't, with patience for the slow parts. She filled it with strangeness and color and human longing, with characters who wanted things they couldn't quite reach, with doors that opened onto other doors.

And when the gray light appeared at the window she stopped.

She stopped at the exact moment the king could not bear to stop.

"Sister," said Dunyazade, "finish the tale." And Scheherazade said: "I cannot. The king must decide." And she was quiet and her eyes were down and she did not look at him, and the silence lasted a long time.

He spared her until the next night to finish the story. That was all. Just until tomorrow.

That was all she needed.`,
      journal: "Have you ever used words to get through something where force or argument wouldn't have worked?"
    },
    {
      number: 2,
      title: "The Thousand Nights",
      story: `Night after night she came back.

She told tales inside tales inside tales — a fisherman who released a spirit from a jar, a spirit who had been imprisoned for so long he had grown past anger into something more dangerous, a king who heard the story and made a wrong judgment, a vizier who warned him with another story, and inside that story another story still, nested like boxes, each one containing a human life, each one opening into another. The stories were inexhaustible because she was inexhaustible, because she had spent her whole life filling herself with them against this exact hour.

She was never careless. She knew — in the way that someone who has thought very clearly about a very serious problem knows — that a careless night would be her last night. So she was not careless. She was precise and she was generous and she was endlessly inventive, and she paced each tale with the skill of someone who understands that what people fear most is not the end of a story but the possibility that the story will end before they are ready.

She told him about Ali Baba and the cave of wonders. She told him about the sailor Sinbad who went seven times to sea and seven times into catastrophe and seven times came home with more than he'd left with. She told him about the prince who fell in love with a sleeping girl and about the girl who was not sleeping at all but only waiting for someone brave enough to reach her. She told him about genies in bottles and magic horses and islands that turned out to be whales and merchants who could understand the language of animals and kingdoms under the sea.

She told him, inside all of these stories, about justice. About what mercy looks like when it is practiced by someone with power. About what a kingdom feels like when the king is whole. About trust, and what it costs, and what it builds when it is kept.

She never said: you are that merchant. You are that king. You made wrong judgments and here is what they cost. She didn't need to say it. The story said it. The story always says it, if you tell it right.

Some nights she was afraid. Not of him — she had moved past that particular fear on the first night, when she made the decision she made. She was afraid sometimes that the stories would run out, that she would reach the bottom of herself and find nothing more. On those nights she reached deeper than she'd reached before, into the oldest parts of what she'd read and remembered, into the stories that are true because they have always been true and always will be, the ones about what people are really like underneath the things that happen to them.

She always found something.

The nights accumulated. She and Dunyazade grew to know each other in a way that sisters who live ordinary lives do not always know each other — in the dark, in the quiet before the tale began, in the waiting. The king grew accustomed to the sound of her voice in the dark. He did not examine this too closely. He simply found that he did not want the nights to end.

She gave birth to three sons during those years. She raised them in the palace. She went on telling stories.

The wound in the king was not gone. Wounds don't vanish. But a wound that is surrounded by enough living, enough story, enough patience — it loses some of its power to define everything. The king began to be, again, something more than the thing that had happened to him.

She had done this. She had done it one night at a time, with nothing but language and intelligence and the refusal to let the darkness have the last word.`,
      journal: "What is something you have kept going, night after night or day after day, without being sure how it would end?"
    },
    {
      number: 3,
      title: "One Thousand and One",
      story: `When one thousand and one nights had passed, Scheherazade said to the king: "I have told you many tales. But now I have one request to make of you."

He said: "Tell me what it is."

She brought to him their three sons — one walking, one crawling, one at the breast. She set them before him on the floor of the chamber where she had told her stories, in the light of the morning that had come a thousand and one times and found her still alive, still speaking, still present.

She said: "O king, these are your children. I ask you, for their sake, to spare their mother."

There was a long silence.

He wept.

He said: "By God, Scheherazade, I had already pardoned you before the children came. For I found you chaste and pure and wise and eloquent. God bless you and bless your father and your mother."

He called for the court to assemble. He told them everything — the years of his grief, the cruelty it had made him, the thousand and one nights, the woman who had sat in the dark and told him back into himself. He said: "This woman is my queen, and will be my queen until one of us is in the ground."

The people rejoiced. They had been grieving for three years. They had lost daughters and sisters and neighbors to a king's wound that he had worn like armor, mistaking it for strength. Now the armor was off. They could see him again.

The tales were written down. Scholars gathered them and annotated them and argued about which were original and which were added and in which century they had passed through which city, and all of that is interesting but it is not the story.

The story is a woman in a dark room, with her sister hidden in the shadows, and a man whose grief had made him monstrous, and nothing between her and morning but her voice.

She used it. Night after night she used it. She had been preparing for it her whole life without knowing she was preparing, and when the moment came she was ready, and she was not afraid, and she did not falter.

She ran out of stories, finally. She offered up her sons and her clear eyes and asked plainly for her life.

And what she had built over those thousand nights — the long patient work of a woman who understood that a story, told well and honestly and without ceasing, can change what a person believes about the world — held.

She had saved the women of the kingdom. She had saved the king from himself. She had done it with language and intelligence and the particular courage of someone who knows that the only way through is through, and begins.

The record of those nights has never been lost.`,
      journal: "The storyteller ran out of stories and asked plainly for what she needed. What would you ask for, plainly, if you knew someone was really listening?"
    }
  ]
},
{
  id: "beauty",
  title: "Beauty and the Beast",
  era: "Madame de Villeneuve, 1740 — Madame de Beaumont, 1756",
  subtitle: "La Belle et la Bête",
  color: "#60D4B4",
  quote: "She is not where she thought she'd be, but somehow exactly where she needs to be.",
  attribution: "Morgan Harper Nichols",
  chapters: [
    {
      number: 1,
      title: "The Rose",
      story: `There was once a merchant, very rich, with six children — three sons and three daughters. All of them were beautiful, but the youngest daughter was so lovely that she was called simply Beauty, and the name fit her so completely that no one ever thought to argue with it. She was also, and this the story considers equally important, good. Not good in the way of someone who has never been tested. Good in the way of someone who has chosen, each time the choice came, to be kind rather than cruel, patient rather than bitter, grateful rather than envious.

Her sisters were none of these things. They were beautiful and they knew it and they had arranged their lives around the fact of it, the way some people arrange their lives around money or a talent for music. They expected the world to accommodate them. Often it did.

Then the merchant lost everything. The ships sank. The warehouses burned. The lawsuits came like wolves.

They moved to a small house in the country, and the sisters made of this a catastrophe and mourned their fine clothes and their admirers and their previous life with the energy that some people give to genuine grief. Beauty woke before dawn and made the fires and cooked the food and kept the garden and spun in the evenings and did not complain, because she had understood something her sisters hadn't: that the life in front of you is the only life there is, and you can meet it or you can mourn the life you used to have, but you cannot do both at once.

A year passed. Then news came that one of the merchant's ships had arrived in port with its cargo intact. He would go to the city and perhaps recover something. His daughters begged him for gifts — fine dresses, pearls, jewels. Beauty asked for nothing.

"Nothing?" her father said.

"Nothing," she said. "Only — if you pass a rosebush, bring me a rose. They don't grow here."

He did not recover much in the city. On the way home, lost in a great forest in a storm, he found a palace — lit, warmed, set for supper as though he'd been expected. He spent the night in warmth and comfort and in the morning, on his way out through the garden, he saw a rose arbor and remembered his daughter's request and picked one stem.

A Beast appeared.

It was enormous and terrible and it spoke, which was almost worse than the size and the terror. It said: "You come to my house and eat my food and sleep in my bed and steal my roses. Is this how you repay hospitality?"

The merchant wept. He explained the rose was for his youngest daughter. He begged forgiveness.

The Beast said: "I will spare you. But one of your daughters must come to live with me. She must come willingly. If she refuses, you must return yourself to die."

The merchant went home and told them everything. He had expected — had planned, even — to return to the Beast himself and die, for he could not ask his children to take his punishment. But Beauty would not hear of it.

"I asked for the rose," she said. "I'll go."

And she meant it. She had looked at the alternatives — her father dead, her sisters weeping but ultimately fine, the Beast waiting in his palace — and she had decided. It was not the decision of someone who has given up. It was the decision of someone who has calculated what love costs and is willing to pay it.`,
      journal: "Has someone ever taken something hard upon themselves so you wouldn't have to? Or have you ever done that for someone else?"
    },
    {
      number: 2,
      title: "The Palace",
      story: `The Beast's palace was extraordinary. Not in the way of ordinary wealth — not just gold and fine tapestries, though there was plenty of that — but in the way of a place that has been arranged for a person. Beauty had a room full of books. She had a harpsichord and music she'd never seen. She had a mirror that showed her the people she loved in her father's house, when she was too lonely and needed to look. She had every dress she could have wanted, every fruit, every flower. The palace had known she was coming and had prepared.

This is worth dwelling on. The palace prepared for her. Whatever the Beast was — whatever had made him the way he was, whatever enchantment had built this place on the edge of the world — it had thought about what she would need. It had considered her. In her father's house, in her ordinary life, nobody had ever quite done that. She had been the one who considered others.

She was afraid at first. That would be dishonest to leave out. The Beast was enormous and moved through the palace like a storm moving through a forest — everything still while it passed, and then the stillness of aftermath. But he did not hurt her. He showed her the gardens. He asked what she had thought of the room he'd given her. He listened to her answers with a seriousness that surprised her.

Each evening he came to supper with her. They talked. He was not clever — he seemed to know this about himself and did not try to be what he wasn't, which Beauty found, to her surprise, restful. She was so accustomed to people performing themselves at her. He simply was what he was, and what he was was honest, and lonely, and kind in the way of something that has not had enough practice at kindness but is trying.

Each evening, at the end of supper, he asked: "Beauty, will you marry me?"

Each evening she said no. She said it gently but she said it clearly, because she was honest too, and she had no feeling for him of that kind. He would say: "Then goodnight, Beauty." And he would leave.

But she looked forward to the evenings. She discovered this about herself slowly: that she looked forward to them. There was something in the conversation — in his attention, in his honesty, in the way he looked at her as though she were the only real thing he had encountered in a long time — that was more nourishing than the fine food and the beautiful rooms.

She missed her family. She asked to visit them and the Beast said yes, with one condition: she must return in eight days. He gave her a ring and said: "Turn it on your finger when you are ready to come back."

He said — and he said it simply, without drama or manipulation — "If you do not return in eight days I will die of grief. I cannot help it. It is what I will do."

She went home. Her sisters received her with a kind of joy that had jealousy underneath it, the way a gift can have conditions underneath it. They saw her fine clothes and her good health and her contentment — for she was content, she realized, in the palace; she had been genuinely content — and they conspired together to keep her past the eight days. They wept and begged and said how they needed her, how they couldn't bear to lose her again. And Beauty, who loved them, stayed.

On the tenth day she dreamed of the Beast lying in the garden, dying.

She woke and turned the ring.`,
      journal: "Have you ever stayed somewhere past the time you should have left, because someone needed you to? What brought you back?"
    },
    {
      number: 3,
      title: "What She Loved",
      story: `She woke in the palace. It was midnight and the rooms were dark and she ran through them calling for the Beast and he did not answer.

She searched the house. She searched the garden. She found him lying in the rose arbor where her father had once stood and picked a flower — lying on the ground, eyes closed, barely breathing, the way something enormous and alive looks when it is failing. Like a storm that has finally spent itself. Like a fire going out.

She threw herself on the ground beside him. She took his great ugly head in her hands. She said: "Don't die. Please don't die. I didn't know — I should have come back sooner — please."

His eyes opened. He said: "I thought you'd forgotten me. I could not bear it. But I am glad to have seen you once more."

She said: "You shall not die. You will live and be my husband. I thought what I felt was only friendship, but I know now it is more than that. I cannot — I find I cannot be anywhere you aren't."

She meant it. She had discovered this the way you discover things that have been true for a while before you know they're true: slowly, and then all at once.

The night blazed. The garden blazed. The palace blazed with light from every window. Where the Beast had lain a man rose up — young, and handsome, and looking at her with the same eyes. The same eyes exactly. She recognized them.

He told her everything. A wicked enchantment, long ago, by someone who had wanted to diminish him and had taken the wrong form as the instrument. He had been told he could not be freed until a beautiful girl loved him of her own free will, with full knowledge of what he was.

Beauty looked at him a long moment. "I loved the Beast," she said. "I want to be sure I am not losing him."

"You are not losing him," the prince said. "He is what I always was, inside it. The form has changed. What you loved is here."

She believed him. Because she had learned, in that palace, to look past the surface of things. She had learned it slowly and through daily practice — through evenings of conversation, through watching what someone does when they think no one important is watching, through paying attention to the small things that tell you more than the large things do. She had come to the palace afraid and she had stayed and looked and found what was actually there, underneath the frightening form, underneath the loneliness, underneath the years.

What was there was worth loving. It had been worth loving all along.

Her father came to live in the palace. Her sisters were there for the wedding, and what happened to them afterward the story declines to specify, except to say that their envy was not rewarded, which is the only thing the story thinks worth saying about envy.

Beauty married the prince who had been the Beast who had been the prince. She had come to a place she didn't choose and she had stayed and paid attention and found something real. She had gone home when she was homesick and come back when she understood she couldn't stay away.

She had learned what she loved by almost losing it. That is one of the oldest and most expensive lessons there is. She learned it young enough to do something with it.

That is the whole story. It is enough.`,
      journal: "The story says she learned what she loved by almost losing it. Has that ever been true for you — of a person, a place, a way of living?"
    }
  ]
},
{
  id: "tatterhood",
  title: "Tatterhood",
  era: "Peter Christen Asbjørnsen and Jørgen Moe, 1845 — Norwegian folk tradition",
  subtitle: "A Norse Tale",
  color: "#7DD3FC",
  quote: "Well-behaved women seldom make history.",
  attribution: "Laurel Thatcher Ulrich",
  chapters: [
    {
      number: 1,
      title: "The Two Girls",
      story: `Once upon a time there was a king and a queen who had everything a king and queen could want, except the one thing they wanted most: a child. They had prayed for years and nothing came.

One evening a beggar woman came to the palace door, and the queen received her kindly and gave her food and drink and a warm place to sleep. In the morning the beggar woman said: "You have been good to me, and I will be good to you in return. Tonight, when you bathe, throw the water out under the bed, and in the morning you will find two flowers — one beautiful and one ugly. You must not pick the ugly one, whatever you do. Pick only the beautiful one."

The queen did as she was told. In the morning she found the two flowers. She picked the beautiful one. But then she thought: if the ugly one also brings a child, why should I not pick it too? And so she picked it.

She gave birth to two daughters at the same time.

The first came into the world gently — fair and quiet, fine-featured, the kind of beautiful that makes people step back and then step forward again, wanting to be near it.

The second came in howling, with a wooden spoon in her hand, already astride a goat she had apparently brought with her from wherever she'd come from. She set up an immediate and tremendous noise.

"What is that?" the midwife said.

"That," said the queen, with a love that surprised her by being completely equal in both directions, "is my daughter."

They called her Tatterhood. They called the other one by a proper name, which the story has somehow lost, so we will call her the Princess, which she was.

Tatterhood grew up riding her goat through the king's hall, knocking things over, making an uproar, wearing her old gray hood and carrying her wooden spoon everywhere she went. She was loud. She was rough. She had opinions she expressed immediately and at full volume. The court found her alarming. The court told the king. The king told the queen. The queen said: "She is my daughter," and that was the end of the conversation.

The Princess was gentle and beloved. She was the one people naturally moved toward, the one who made rooms feel easier, the one who laughed at the right moments and asked the right questions and had a gift for making whoever she was talking to feel that they were the most interesting person she'd encountered all day. She was not performing this. She was genuinely like this. She loved her wild sister completely.

And Tatterhood loved her back in the way of someone who would ride into the world's worst darkness and back out again without being asked twice.

Which, as it turned out, was exactly what was needed.

Christmas Eve came. The palace at Christmas Eve is full of candles and warmth and the smell of food and the old feeling of safety that comes from winter held at bay. But outside, in the dark, things were gathering. The story says witches. What it means is: the kind of force that comes for people when they are most settled and least expecting it, the thing that arrives when you've begun to believe you are safe.

They came through the palace. They came through every room. And in the confusion, in the chaos of screaming and running and overturned candlesticks, one of them got hold of the Princess's head and put a calf's head in its place.

When it was over and the witches had gone, there was the Princess, with a calf's head where her own face should have been, looking out of confused bovine eyes at the world she had moved through so gracefully such a short time before.

Tatterhood looked at her sister. She looked at the door the witches had gone out through.

She got on her goat.

"I'm going to get it back," she said, to no one in particular. "Don't try to stop me."

No one did.`,
      journal: "Is there someone you would go after something terrible for, without needing to think about it first?"
    },
    {
      number: 2,
      title: "The Sea",
      story: `Tatterhood went after the witches in a little boat, on a dark sea, at Christmas, alone. The story doesn't dwell on this — it has the matter-of-fact tone of someone recording what happened — but let it be said plainly: she sailed alone in the dark after a force that had overwhelmed a palace guard and taken her sister's face, with a wooden spoon and a goat and whatever it is that lives in a person who has decided they are going and that's that.

She found the witches. She went in among them. The story doesn't specify what she did there, only that she found what she was looking for. She put the Princess's head back. She came home with her sister restored, sailing out of the dark water back toward the harbor lights of home.

This is the middle of the story, and it is where most people would say the interesting part is over. The quest completed. The sister saved. The boat coming home.

But the story continues, and what it contains next is stranger and more interesting than the battle with witches, because it is about the thing that happens when someone who has never needed anyone to see them a certain way is suddenly seen.

As they came into harbor, another ship came out to meet them — a king from a neighboring country, with his two sons. The king's elder son was to see about a bride. He had not expected this particular morning.

The younger son saw the Princess and moved toward her the way certain people move toward certain other people — with the specific directness of someone who does not know yet what they've found but is certain they've found something. They talked. They got on well.

The elder prince found himself sailing alongside Tatterhood.

She was riding her goat up and down the deck. Her gray hood was pushed back. She was carrying her wooden spoon. She was, by any conventional measure, a spectacle. He looked at her with the uncertain attention of someone who has been told what beautiful looks like and is finding that the information doesn't quite apply here.

He said — because he was trying, at least — "Why do you ride that ugly beast?"

Tatterhood looked at him. She said: "Is it ugly? Isn't it a fine horse?"

And in the same moment the goat was a fine horse. A gray one, well-formed, moving with the easy confidence of an animal that has always been exactly what it is.

The prince stared.

He said: "Why do you carry that ugly stick?"

She said: "Is it ugly? Isn't it a silver wand?"

And it was.

He looked at her more carefully now. Something was happening and he couldn't name it yet. He said: "Why are you so wild and ugly?"

She said: "Am I? Don't you think I'm pretty?"

And she was. She was beautiful — not despite herself but as herself, having decided that this was the moment for it, having turned toward her own beauty the same way she turned toward everything else: when she was ready, on her own terms, not before.

He understood then, or began to. The horse and the wand and the beauty had been hers all along. She hadn't hidden them. She simply hadn't needed them until now, and now she had decided she did, and so they were there.`,
      journal: "Have you ever changed how you showed up somewhere — not because you had to, but because you decided it was time?"
    },
    {
      number: 3,
      title: "Her Own Terms",
      story: `They came ashore. Both weddings were celebrated at the same time, in the same hall, which is tidier than life usually manages but which the story apparently decided was how it should be.

The Princess married her prince, which suited them both well and was the kind of match that looks inevitable to everyone watching from the outside.

Tatterhood married hers. He stood beside her in the great hall, still looking at her with the slightly dazzled expression of someone who has learned something in the last several hours that has reorganized how they understand the world. This is not a bad way to begin.

The story ends at the wedding feast. It gives us nothing of what came after — no description of the kingdom they made together, no inventory of the deeds she did or didn't do, no accounting of the years. Just the feast, and the lights, and the two couples, and then the familiar words: they lived happily ever after.

But the story, taken whole, is about something the ending doesn't say.

It is about the girl who came into the world already in motion, already holding her instrument, already astride her strange conveyance, making a noise that alarmed everyone in the room. Who grew up refusing to be smaller than she was, louder than she was, stranger than she was. Who rode her goat through halls that were not made for goats and did not apologize. Who went out alone into a dark sea after her sister's face and came back with it, not because someone sent her and not because someone asked but because it was her sister and the witches had gone that way.

Who had a fine horse in her the whole time. Who had a silver wand in her the whole time. Who had beauty in her the whole time and produced it when she felt like it, not when she was told to.

There are girls who spend their whole lives being asked to be less than they are. Quieter. Smoother. More like what people have in mind when they picture the kind of girl they want standing in the room. The request is not always cruel. Sometimes it is well-meaning. Sometimes it comes from people who love you and genuinely think they are helping you.

The request is still wrong.

Tatterhood knew this from the beginning, from the first moment in the world when she came in howling with her spoon and her goat and her tremendous noise. She never forgot it. She let the world try to make her something it could manage more easily, and then she got on her goat and rode.

The wooden spoon was always a silver wand. She just didn't need it to be, until she did.`,
      journal: "What have you been carrying in a form that looks like nothing much — that might be something else entirely, if you decided it was time?"
    }
  ]
},
{
  id: "goosegirl",
  title: "The Goose Girl",
  era: "Brothers Grimm, 1812 — Germanic folk tradition",
  subtitle: "Die Gänsemagd",
  color: "#C084FC",
  quote: "No one can make you feel inferior without your consent.",
  attribution: "Eleanor Roosevelt",
  chapters: [
    {
      number: 1,
      title: "The Handkerchief",
      story: `There was once an old queen whose husband had been dead for many years. She had a daughter, and when the time came for the daughter to marry, a prince from a far country was chosen, and the daughter would have to go to him, which is what daughters had to do in those times.

The queen packed up many beautiful things — gold and silver, cups and jewelry, everything that belonged to a royal dowry. She also took a white handkerchief and pressed three drops of her own blood onto it and gave it to her daughter and said: "Keep this carefully. It will be a talisman for you on your journey."

She also gave her a horse called Falada, who could speak.

The daughter rode out with the waiting-maid, and they rode on together. There was a long way to go. It was the kind of journey that reveals things — about landscape, about weather, and about the people you're traveling with.

The waiting-maid had been watching the princess for a long time. She had a plan she'd been carrying the way you carry a stone in your pocket, something heavy that you're always aware of, that you turn over in your hand in quiet moments. She had been waiting for the right conditions. Long road. No witnesses. The princess vulnerable and far from home.

The first day, the princess was thirsty and asked the waiting-maid to fetch water in her golden cup. The maid said: "If you're thirsty, get it yourself. I'm not your maid." The princess was gentle-natured and didn't know how to respond to this — not because she was foolish but because she had been raised in a place where people treated each other with care, and cruelty still surprised her. So she dismounted and knelt by the stream and drank directly from the water, without the cup.

While she bent to drink, the handkerchief fell from her pocket and the water took it downstream before she looked up.

The waiting-maid saw it happen. She felt it the way a chess player feels when the board suddenly opens.

From that moment she pressed harder. She made the princess give up her horse. She made her put on the maid's clothes and ride the maid's horse. She made her swear by the sky and the earth never to tell a living soul what had been done to her.

The princess swore. Her word meant everything to her — she had been raised in a world where it did. And the waiting-maid had known this about her and had aimed directly for it.

They arrived. The maid was received as the princess. She was given rooms and welcome and the attention of the young king. The true princess stood in the courtyard in rough clothes, unrecognized, holding onto nothing. She had lost her horse and her clothes and her handkerchief and her place. She had only her word, locked around her like a cage she'd built herself.

The young king looked out of his window and saw her standing in the courtyard. He went to the false bride and said: "Who is that girl?" The false bride, without blinking, said: "I brought her along for company. Give her something to do."

So the king gave her something to do.

He gave her the geese.`,
      journal: "Have you ever lost something that felt like your proof — of who you were, of where you belonged, of what you were owed?"
    },
    {
      number: 2,
      title: "The Meadow",
      story: `Every morning before the sun was fully up, the goose girl went through the city gate with the geese and the boy called Conrad who helped tend them. And every morning, passing through the gate, she looked up at the arch above her.

Because the maid had ordered Falada killed. The one witness. The one who had been with her from the beginning, who had known her as she was before the road stripped everything away. She could not stop the killing. She could only ask the master of the hounds, quietly and with what she had left of dignity, if Falada's head could be hung above the gate, so she could see it when she passed.

He had done her this kindness.

So each morning she looked up at the arch and said:

"Falada, Falada, there thou art hanging."

And the head answered from where it hung:

"Alas, alas, young queen, that thou should'st hang here. If thy mother knew it, her heart would break in two."

Then she went into the meadow and sat in the grass and let her hair down. Her hair was gold — real gold, the kind that the light moves inside. Conrad wanted to pull some of it out. She said, under her breath, to the wind she'd always been able to call:

"Blow, wind, blow, take Conrad's hat, and make him run as fast as that."

And the wind came, Conrad's hat went sailing across the meadow, and he chased it, and by the time he came back her hair was braided again and she was watching the geese.

This happened for several days. Conrad finally went to the king and complained about the strange goose girl who talked to a dead horse's head and called the wind to take his hat.

The king told Conrad to go again tomorrow. He said he would come himself, secretly, and watch.

Think about what she was doing in those meadow mornings, and what it cost her. She was, technically, working. She was keeping the geese together, counting them, bringing them home in the evenings, doing the job she had been given. But she was also doing something else. She was keeping herself.

The conversation with Falada's head was not a conversation with a dead horse. It was a conversation with the person she had been, with the life she had lived before the road, with the truth about herself that she could not say to any living person. She said it to Falada so it could be said. So it could exist somewhere outside her own mind. So she wouldn't lose it.

The hair was the same. She let it down each morning because it was hers. It was still gold. No one could take that from her by giving her rough clothes and a job with the geese. Her hair was still her hair. She let it down in the meadow where no one was watching and she was, for a few minutes each morning, completely herself.

This is the thing the story is actually about. Not the enchantment. Not even the injustice, though the injustice is real and matters. The story is about a girl who has had everything taken from her — her name, her position, her horse, her clothes, her handkerchief with her mother's blood — and who is in a meadow with geese and a boy who wants to pull her hair, and who keeps herself intact anyway.

She does not know how long this will last. She does not know if it will ever end. She knows only that Falada's head says true things when she speaks to it, and that her hair is still gold, and that the wind comes when she calls.

She tends the geese. She keeps herself.

The king comes and watches from the gate.`,
      journal: "Is there something you do — quietly, privately, perhaps where no one sees — just to remember who you are?"
    },
    {
      number: 3,
      title: "The Iron Stove",
      story: `The king watched from the gate. He heard her speak to the horse's head. He heard the head speak back. He watched her let down her hair in the meadow and call the wind, and he watched Conrad chasing his hat across the grass, and he watched her braid it again and go back to the geese as though none of it had happened.

He went back to the palace. He had her brought to him.

He asked her: "Who are you? What are these things I have seen?"

She said: "I cannot tell you. I swore by the sky and the earth not to tell any living person, and I will not break my oath." She said this simply, without drama, looking at him steadily. She had been a princess long enough to know how to stand before a king.

He said: "Then tell your grief to the iron stove in the corner. Tell it everything."

He left the room. He crept around to the outside of the stove's pipe.

She went to the stove. She crouched beside it. And she told it everything — how she was a princess, how the waiting-maid had robbed her on the road, how she had been made to swear, how she had been put to tend the geese, how every morning she talked to Falada's head because it was all she had left of her real life, because she needed to say the true things to something, even if the something was iron and the only ear was the wind.

She told it about her mother. About the handkerchief and the three drops of blood and how they had meant to be her protection, and how she had lost them before she understood what she had.

She told it about the meadow. About the gold hair she let down each morning not because it was useful but because it was hers.

The king heard everything. He came back in. He called the court together and the false bride was brought before them all, and the king said: "What should happen to a person who does such and such?" And the false bride, not knowing she was speaking about herself, named the worst punishment she could imagine.

The king said: "You have sentenced yourself."

The true princess was given her proper clothes and her proper place. Falada, the story says — in one of those small mercies that fairy tales sometimes allow — Falada was brought back, restored.

She married the young king. She had been the goose girl for a season, tending the geese through the autumn grass, speaking to the dead horse's head each morning, letting her gold hair down in the meadow, keeping herself intact through all of it with the quiet stubbornness of someone who knows that the truth about herself is real even when no one around her knows it.

The truth had not needed defending. It had only needed keeping.

She had kept it.

The story ends, as these stories do, with the wedding. The kingdom restored. The false bride punished. The old king who had first seen her in the courtyard satisfied that he had been right to wonder about her.

But the real end of the story happened in the meadow. It happened each morning when she stood at the gate and said: Falada, Falada, there thou art hanging. It happened each morning when the voice answered her back and told her what was true. It happened when she called the wind. It happened in the small, private, daily act of keeping herself present inside a life that was not her life, of tending the flame until the moment came when someone was finally listening.

She had known who she was the whole time.

She had just been waiting for the world to know it too.`,
      journal: "The truth about her was real the whole time, even when no one around her knew it. What is something true about you that isn't fully seen yet?"
    }
  ]
}
];

// ─── EXPLORE TAB ─────────────────────────────────────────────────
function ExploreTab({ C, onAddJournalEntry, storyProgress, onUpsertProgress }) {
  const [story, setStory] = useState(null);

  if (story) return <StoryReader C={C} story={story} onExit={()=>setStory(null)} onAddJournalEntry={onAddJournalEntry} savedChapter={storyProgress?.[story.id] ?? 0} onUpsertProgress={onUpsertProgress} />;

  return (
    <div style={{padding:"20px 20px 20px"}}>
      <div style={{textAlign:"center",marginBottom:24}}>
        <div style={{fontFamily:"Pacifico, cursive",fontSize:26,background:`linear-gradient(90deg,${C.accent},${C.purple})`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",marginBottom:6}}>Explore</div>
        <div style={{color:C.sub,fontSize:13,fontWeight:600,lineHeight:1.6,maxWidth:300,margin:"0 auto"}}>Ancient stories. Each one written for a girl like you.</div>
      </div>
      {EXPLORE_STORIES.map(s => {
        const reached = storyProgress?.[s.id];
        const started = reached !== undefined && reached !== null;
        const complete = started && reached >= s.chapters.length;
        const statusLabel = complete ? "Complete ✨" : (started ? `Part ${Math.min(reached+1, s.chapters.length)} of ${s.chapters.length}` : "Start →");
        return (
          <button
            key={s.id}
            onClick={()=>setStory(s)}
            style={{display:"block",width:"100%",textAlign:"left",background:C.card,border:`1px solid ${C.border}`,borderRadius:18,padding:18,marginBottom:14,cursor:"pointer",position:"relative",overflow:"hidden",fontFamily:"Nunito, sans-serif",boxShadow:`0 2px 12px ${C.shadow}`}}
          >
            <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:s.color,borderRadius:"18px 18px 0 0"}}/>
            <div style={{color:C.sub,fontSize:11,fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>{s.era}</div>
            <div style={{fontFamily:"Pacifico, cursive",fontSize:24,color:C.text,marginBottom:2}}>{s.title}</div>
            <div style={{color:C.sub,fontSize:13,fontStyle:"italic",fontWeight:600,marginBottom:8}}>{s.subtitle}</div>
            <div style={{color:s.color,fontSize:11,fontWeight:800,letterSpacing:1.5,textTransform:"uppercase"}}>{statusLabel}</div>
            <div style={{position:"absolute",right:18,top:"50%",transform:"translateY(-50%)",color:s.color,fontSize:18,fontWeight:800}}>→</div>
          </button>
        );
      })}
    </div>
  );
}

// ─── STORY READER ────────────────────────────────────────────────
function StoryReader({ C, story, onExit, onAddJournalEntry, savedChapter, onUpsertProgress }) {
  const initialComplete = savedChapter >= story.chapters.length;
  const initialChapter = initialComplete ? story.chapters.length - 1 : Math.max(0, Math.min(savedChapter, story.chapters.length - 1));
  const [chapter, setChapter] = useState(initialChapter);
  const [phase, setPhase] = useState(initialComplete ? "final" : "story"); // story | journal | final
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [progressError, setProgressError] = useState("");
  const scrollRef = useRef(null);

  const ch = story.chapters[chapter];
  const isLast = chapter >= story.chapters.length - 1;
  const progress = phase === "final" ? 1 : (chapter + 0.5) / story.chapters.length;

  useEffect(()=>{ if (scrollRef.current) scrollRef.current.scrollTop = 0; }, [chapter, phase]);

  const persistProgress = async (storyId, idx) => {
    const result = await onUpsertProgress?.(storyId, idx);
    if (result?.error) setProgressError(`Couldn't save progress: ${result.error}`);
    else setProgressError("");
  };

  const goJournal = () => { setDraft(""); setSaveError(""); setPhase("journal"); };
  const skipJournal = () => advance();
  const advance = () => {
    if (isLast) {
      setPhase("final");
      persistProgress(story.id, story.chapters.length);
    } else {
      const next = chapter + 1;
      setChapter(next);
      setPhase("story");
      persistProgress(story.id, next);
    }
  };

  const saveAndContinue = async () => {
    const text = draft.trim();
    if (!text) { advance(); return; }
    setSaving(true);
    setSaveError("");
    const prompt = `${story.title} · Part ${ch.number} — ${ch.title}\n${ch.journal}`;
    const { error } = await onAddJournalEntry({ mood: 3, text, prompt });
    setSaving(false);
    if (error) { setSaveError(error); return; }
    advance();
  };

  const back = () => {
    if (phase === "journal") { setPhase("story"); return; }
    if (phase === "final") { setPhase("story"); return; }
    if (chapter > 0) { setChapter(c=>c-1); return; }
    onExit();
  };

  const headerTitle = phase === "journal" ? `${story.title} — Part ${ch.number}` : story.title;
  const paragraphs = ch.story.split(/\n\n+/).map(p=>p.trim()).filter(Boolean);

  return (
    <div ref={scrollRef} style={{height:"100%",overflowY:"auto",WebkitOverflowScrolling:"touch",background:C.bg}}>
      {/* Header */}
      <div style={{padding:"12px 20px 10px",display:"flex",alignItems:"center",gap:12,position:"sticky",top:0,background:C.bg,zIndex:5}}>
        <button onClick={back} style={{background:"none",border:"none",color:C.sub,fontSize:22,cursor:"pointer",padding:4,lineHeight:1,fontFamily:"Nunito, sans-serif"}}>←</button>
        <span style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase"}}>{headerTitle}</span>
      </div>
      {/* Progress strip */}
      <div style={{height:3,background:`${C.fade}`,position:"relative",margin:"0 20px",borderRadius:2}}>
        <div style={{height:"100%",width:`${progress*100}%`,background:`linear-gradient(90deg,${C.accent},${story.color})`,transition:"width 0.4s ease",borderRadius:2}}/>
      </div>

      {progressError && (
        <div style={{margin:"12px 20px 0",background:`${C.coral}14`,border:`1px solid ${C.coral}55`,borderRadius:12,padding:"10px 14px",color:C.coral,fontSize:12,fontWeight:700,lineHeight:1.5}}>
          {progressError}
        </div>
      )}

      {phase === "final" ? (
        <div style={{padding:"48px 24px 100px",textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",gap:16}}>
          <div style={{fontSize:56}}>✨</div>
          <div style={{fontFamily:"Pacifico, cursive",fontSize:32,color:C.text,lineHeight:1.2}}>{story.title}</div>
          <p style={{color:C.sub,fontSize:14,fontWeight:600,lineHeight:1.7,maxWidth:280,margin:0}}>You walked through all three parts of this story. Whatever you wrote is yours to keep.</p>
          <div style={{fontFamily:"Pacifico, cursive",fontSize:18,color:story.color,lineHeight:1.6,marginTop:8}}>
            The story isn't finished.<br/>Neither are you.
          </div>
          <button onClick={onExit} style={{marginTop:16,width:"100%",maxWidth:300,background:`linear-gradient(135deg,${C.accent},${story.color})`,border:"none",color:"white",borderRadius:999,padding:"14px",fontSize:15,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif",boxShadow:`0 4px 16px ${C.shadow}`}}>Back to Explore →</button>
        </div>
      ) : phase === "journal" ? (
        <div style={{padding:"24px 20px 100px"}}>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:18,padding:20}}>
            <div style={{color:C.sub,fontSize:14,lineHeight:1.7,fontStyle:"italic",fontWeight:600,marginBottom:16}}>{ch.journal}</div>
            <textarea
              value={draft}
              onChange={e=>setDraft(e.target.value)}
              placeholder="Write whatever comes. There's no wrong answer."
              style={{width:"100%",background:C.inputBg,border:`1px solid ${C.border}`,borderRadius:12,padding:14,color:C.text,fontFamily:"Nunito, sans-serif",fontSize:14,fontWeight:600,lineHeight:1.7,resize:"none",minHeight:160,outline:"none",boxSizing:"border-box"}}
            />
          </div>
          {saveError && <div style={{color:C.coral,fontSize:13,fontWeight:700,marginTop:12,textAlign:"center"}}>{saveError}</div>}
          <button
            onClick={saveAndContinue}
            disabled={saving}
            style={{marginTop:16,width:"100%",background:`linear-gradient(135deg,${C.accent},${story.color})`,border:"none",color:"white",borderRadius:999,padding:"14px",fontSize:15,fontWeight:800,cursor:saving?"default":"pointer",fontFamily:"Nunito, sans-serif",opacity:saving?0.6:1,boxShadow:`0 4px 16px ${C.shadow}`}}
          >
            {saving ? "Saving…" : (isLast ? "Finish this story →" : "Want to continue? →")}
          </button>
          <button onClick={skipJournal} disabled={saving} style={{marginTop:8,width:"100%",background:"none",border:"none",color:C.sub,fontSize:13,fontWeight:700,cursor:saving?"default":"pointer",fontFamily:"Nunito, sans-serif",padding:10,textDecoration:"underline",textDecorationColor:C.fade}}>Skip for now</button>
        </div>
      ) : (
        <div style={{padding:"20px 20px 100px"}}>
          {chapter === 0 && (
            <div style={{background:C.card,borderLeft:`3px solid ${story.color}`,padding:"14px 16px",marginBottom:24,borderRadius:"0 12px 12px 0"}}>
              <div style={{fontFamily:"Pacifico, cursive",fontStyle:"italic",fontSize:16,color:C.text,lineHeight:1.6,marginBottom:6}}>"{story.quote}"</div>
              <div style={{fontSize:12,color:C.sub,fontWeight:700}}>— {story.attribution}</div>
            </div>
          )}

          {/* Chapter dots */}
          <div style={{display:"flex",gap:8,justifyContent:"center",padding:"8px 0 16px"}}>
            {story.chapters.map((_, i) => {
              const isDone = i < chapter, isCurrent = i === chapter;
              return <div key={i} style={{width:8,height:8,borderRadius:"50%",background:isCurrent?story.color:(isDone?C.accent:C.fade),transition:"background 0.3s"}}/>;
            })}
          </div>

          <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Part {chapter+1} of {story.chapters.length}</div>
          <div style={{fontFamily:"Pacifico, cursive",fontSize:30,color:C.text,lineHeight:1.25,marginBottom:20}}>{ch.title}</div>

          <div>
            {paragraphs.map((p, i) => (
              <p key={i} style={{fontSize:15,color:C.text,opacity:0.92,lineHeight:1.85,marginBottom:16,fontWeight:500}}>{p}</p>
            ))}
          </div>

          <div style={{textAlign:"center",padding:"20px 0 4px"}}>
            <div style={{fontFamily:"Pacifico, cursive",fontStyle:"italic",fontSize:16,color:story.color,lineHeight:1.6}}>The story isn't finished.</div>
            <div style={{fontFamily:"Pacifico, cursive",fontStyle:"italic",fontSize:16,color:story.color,lineHeight:1.6}}>Neither are you.</div>
          </div>

          <button
            onClick={goJournal}
            style={{marginTop:20,width:"100%",background:`linear-gradient(135deg,${C.accent},${story.color})`,border:"none",color:"white",borderRadius:999,padding:"14px",fontSize:15,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif",boxShadow:`0 4px 16px ${C.shadow}`}}
          >
            Write in your journal →
          </button>
          <button onClick={skipJournal} style={{marginTop:8,width:"100%",background:"none",border:"none",color:C.sub,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"Nunito, sans-serif",padding:10,textDecoration:"underline",textDecorationColor:C.fade}}>Continue without writing</button>
        </div>
      )}
    </div>
  );
}

// ─── HOME TAB ────────────────────────────────────────────────────
function HomeTab({C,quote,quoteVisible,onNextQuote}) {
  return (
    <div style={{padding:"20px 20px 0"}}>
      <div style={{marginBottom:20}}>
        <div style={{fontFamily:"Pacifico, cursive",fontSize:26,color:C.text,lineHeight:1.2}}>Good morning ✨</div>
        <div style={{color:C.sub,fontSize:14,marginTop:4,fontWeight:600}}>You showed up. That's everything.</div>
      </div>
      <div style={{background:`linear-gradient(135deg,${C.accent}22,${C.purple}18)`,borderRadius:20,padding:24,marginBottom:20,border:`1px solid ${C.border}`,minHeight:160,display:"flex",flexDirection:"column",justifyContent:"space-between"}}>
        <div style={{fontSize:28,color:`${C.accent}55`,lineHeight:1,marginBottom:8}}>"</div>
        <div style={{opacity:quoteVisible?1:0,transform:quoteVisible?"translateY(0)":"translateY(8px)",transition:"opacity 0.32s,transform 0.32s",flex:1}}>
          <p style={{color:C.text,fontSize:15,lineHeight:1.65,fontStyle:"italic",margin:"0 0 12px",fontWeight:600}}>{quote?.text}</p>
          <div style={{color:C.accent,fontSize:13,fontWeight:800}}>— {quote?.author}</div>
          {quote?.era&&<div style={{color:C.sub,fontSize:11,fontWeight:600,marginTop:2}}>{quote.era}</div>}
        </div>
        <button onClick={onNextQuote} style={{alignSelf:"flex-end",marginTop:12,background:C.accent,border:"none",color:"white",borderRadius:999,padding:"8px 18px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>New Quote ↻</button>
      </div>
      <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>Today's Story</div>
      <div style={{background:C.card,borderRadius:14,padding:18,marginBottom:10,border:`1px solid ${C.border}`,textAlign:"center"}}>
        <div style={{color:C.sub,fontSize:14,fontWeight:600,lineHeight:1.7}}>Begin your journey in the Explore tab ✨</div>
      </div>
    </div>
  );
}

// ─── MOVE TAB ────────────────────────────────────────────────────
function MoveTab({C,screen,setScreen,level,levels,moveLevel,setMoveLevel,sessions,streak,onStart,currentEx,exIdx,exFrac,sec,resting,running,onTogglePlay,onSkip,exercises,location,zipOverride,setZipOverride,setLocation}) {
  const [events,setEvents]=useState([]);
  const [loadingEvents,setLoadingEvents]=useState(false);
  const [locationInput,setLocationInput]=useState("");
  const [showLocationEdit,setShowLocationEdit]=useState(false);

  const locationLabel=zipOverride||( location ? `Near you` : "No location set" );

  const fetchEvents=async()=>{
    setLoadingEvents(true);
    const locStr=zipOverride||( location ? `${location.lat},${location.lng}` : "New York" );
    try {
      const data=await callClaude({model:"claude-sonnet-4-6",max_tokens:2000,
        tools:[{type:"web_search_20250305",name:"web_search"}],
        messages:[{role:"user",content:`Search for women's fitness classes, yoga, dance classes, outdoor group workouts, running clubs, or wellness events near ${locStr} happening this week or soon. Find real, specific events with names, locations, and dates. Return ONLY valid JSON array of 4-5 events: [{"name":"...","type":"...","emoji":"...","when":"...","where":"...","vibe":"...","url":"..."}]. No markdown.`}]
      });
      console.log("[fetchEvents] raw response:", data);
      const textBlock=data.content?.find(b=>b.type==="text");
      if(textBlock) {
        const parsed=JSON.parse(textBlock.text.replace(/```json|```/g,"").trim());
        setEvents(parsed);
      } else { throw new Error("no text block in response"); }
    } catch (err) {
      console.error("[fetchEvents] failed:", err);
      setEvents([
        {name:"Morning Yoga in the Park",type:"Yoga",emoji:"🧘‍♀️",when:"Sat 8:00 AM",where:"Local park",vibe:"Free, all levels, bring a mat",url:""},
        {name:"Girls Run Club",type:"Running",emoji:"🏃‍♀️",when:"Sun 7:30 AM",where:"City trail",vibe:"5K social run, beginners welcome",url:""},
        {name:"Dance Cardio",type:"Dance",emoji:"💃",when:"Wed 6:30 PM",where:"Community studio",vibe:"Fun, sweaty, no experience needed",url:""},
        {name:"Hiking Group for Women",type:"Hiking",emoji:"🌿",when:"Sat 9:00 AM",where:"Nearby trail",vibe:"Community vibes, all paces",url:""},
      ]);
    }
    setLoadingEvents(false);
  };

  const saveLocation=()=>{
    if(locationInput.trim()) { setZipOverride(locationInput.trim()); setLocation(null); }
    setShowLocationEdit(false); setLocationInput("");
  };

  if(screen==="session") return (
    <div style={{height:"100%",background:`linear-gradient(135deg,${C.accent},${C.purple})`,display:"flex",flexDirection:"column",alignItems:"center"}}>
      <div style={{display:"flex",justifyContent:"space-between",width:"100%",padding:"6px 24px 4px",boxSizing:"border-box"}}>
        <button onClick={()=>setScreen("home")} style={{background:"rgba(255,255,255,0.2)",border:"none",color:"white",borderRadius:999,padding:"6px 14px",fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>End</button>
        <span style={{color:"rgba(255,255,255,0.8)",fontSize:13,fontWeight:700}}>{exIdx+1} of {exercises.length}</span>
      </div>
      <div style={{width:"calc(100% - 48px)",background:"rgba(255,255,255,0.2)",borderRadius:4,height:5,margin:"0 24px 8px"}}>
        <div style={{background:"white",borderRadius:4,height:5,width:`${(exIdx/exercises.length)*100}%`,transition:"width 0.5s"}} />
      </div>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-start",textAlign:"center",padding:"0 16px",paddingTop:8,paddingBottom:8,width:"100%",boxSizing:"border-box"}}>
        {resting?(
          <><div style={{fontSize:60}}>🌸</div>
            <div style={{color:"white",fontSize:32,fontWeight:900,marginTop:8,fontFamily:"Pacifico, cursive"}}>Rest</div>
            <div style={{color:"white",fontSize:52,fontWeight:200,marginTop:4}}>{sec}s</div>
            <div style={{color:"rgba(255,255,255,0.75)",fontSize:15,fontStyle:"italic",marginTop:8}}>Breathe. You're doing beautifully.</div>
          </>
        ):currentEx?(
          <><div style={{fontSize:68,marginTop:16}}>{currentEx.emoji}</div>
            <div style={{color:"white",fontSize:22,fontWeight:900,marginTop:8,fontFamily:"Pacifico, cursive"}}>{currentEx.name}</div>
            <div style={{position:"relative",width:140,height:140,margin:"20px auto"}}>
              <svg width={140} height={140} style={{position:"absolute",top:0,left:0,transform:"rotate(-90deg)"}}>
                <circle cx={70} cy={70} r={60} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth={10}/>
                <circle cx={70} cy={70} r={60} fill="none" stroke="white" strokeWidth={10} strokeDasharray={376.99} strokeDashoffset={`${376.99*exFrac}`} strokeLinecap="round" style={{transition:"stroke-dashoffset 0.9s linear"}}/>
              </svg>
              <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:40,fontWeight:200}}>{sec}</div>
            </div>
            <div style={{color:"rgba(255,255,255,0.9)",fontSize:14,lineHeight:1.6,maxWidth:300}}>{currentEx.instruction}</div>
            <div style={{display:"inline-flex",gap:6,background:"rgba(255,255,255,0.15)",borderRadius:999,padding:"7px 16px",marginTop:14}}>
              <span>🫁</span><span style={{color:"white",fontSize:12,fontWeight:700,fontFamily:"Nunito, sans-serif"}}>{currentEx.cue}</span>
            </div>
          </>
        ):null}
        <div style={{display:"flex",alignItems:"center",gap:24,marginTop:20}}>
          <button onClick={onTogglePlay} style={{background:"none",border:"none",cursor:"pointer",fontSize:64,lineHeight:1}}>{running?"⏸":"▶️"}</button>
          <button onClick={onSkip} style={{background:"none",border:"none",cursor:"pointer",fontSize:44,lineHeight:1,opacity:0.6}}>⏭</button>
        </div>
      </div>
    </div>
  );

  if(screen==="complete") return (
    <div style={{height:"100%",background:`linear-gradient(135deg,${C.accent},${C.purple})`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",textAlign:"center",padding:32}}>
      <div style={{fontSize:72}}>🌟</div>
      <div style={{color:"white",fontSize:32,fontWeight:900,marginTop:12,fontFamily:"Pacifico, cursive"}}>You did it!</div>
      <div style={{color:"rgba(255,255,255,0.85)",fontSize:16,marginTop:10,lineHeight:1.7,fontStyle:"italic"}}>You showed up for yourself today.<br/>That is everything.</div>
      <button onClick={()=>setScreen("home")} style={{marginTop:28,background:"white",color:C.accent,border:"none",borderRadius:999,padding:"14px 36px",fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>Back to Home</button>
    </div>
  );

  return (
    <div style={{padding:"20px 20px 0"}}>
      <div style={{fontFamily:"Pacifico, cursive",fontSize:22,color:C.text,marginBottom:16}}>Move & Bloom 🌸</div>

      {/* Level card */}
      <div style={{background:`linear-gradient(135deg,${C.accent}22,${C.purple}18)`,borderRadius:20,padding:20,marginBottom:18,border:`1px solid ${C.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div>
            <div style={{color:C.text,fontSize:17,fontWeight:800}}>{level.label}</div>
            <div style={{color:C.sub,fontSize:13,marginTop:2,fontWeight:600}}>{level.subtitle}</div>
          </div>
          <div style={{display:"flex",gap:6}}>
            {levels.map(l=>(
              <button key={l.id} onClick={()=>setMoveLevel(l.id)} style={{width:28,height:28,borderRadius:"50%",border:"none",background:moveLevel===l.id?C.accent:`${C.accent}22`,color:moveLevel===l.id?"white":C.accent,fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>{l.id}</button>
            ))}
          </div>
        </div>
        <div style={{display:"flex",gap:16,marginTop:14}}>
          {[["🔥",streak,"Streak"],["✅",sessions,"Sessions"]].map(([e,v,l])=>(
            <div key={l} style={{textAlign:"center"}}>
              <div style={{color:C.text,fontSize:16,fontWeight:800}}>{e} {v}</div>
              <div style={{color:C.sub,fontSize:10,fontWeight:700}}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Level-up banner */}
      {sessions >= level.needed && level.id < 5 && (()=>{ const nextLevel=levels.find(l=>l.id===level.id+1); return (
        <div style={{background:`linear-gradient(135deg,${C.accent},${C.purple})`,borderRadius:16,padding:"14px 18px",marginBottom:16,textAlign:"center"}}>
          <div style={{color:"white",fontSize:14,fontWeight:800,lineHeight:1.5}}>You've levelled up to {nextLevel.label} 🎉</div>
          <div style={{color:"rgba(255,255,255,0.88)",fontSize:12,fontWeight:600,marginTop:4}}>Your Morning Bloom just got a little longer.</div>
        </div>
      );})()}

      {/* Session */}
      <div style={{background:C.card,borderRadius:18,padding:20,marginBottom:20,border:`1px solid ${C.border}`,boxShadow:`0 2px 12px ${C.shadow}`}}>
        <div style={{color:C.text,fontSize:16,fontWeight:800,marginBottom:4}}>{level.sessionName} 🌱</div>
        <div style={{color:C.sub,fontSize:13,fontWeight:600,marginBottom:14}}>{exercises.length} moves · ~{Math.round(exercises.reduce((s,e)=>s+(e.base*level.mult),0)/60)} min</div>
        <button onClick={onStart} style={{width:"100%",background:`linear-gradient(135deg,${C.accent},${C.purple})`,border:"none",color:"white",borderRadius:14,padding:"14px",fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>Start Session ▶</button>
      </div>

      {/* Local events */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase"}}>Events Near You</div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button onClick={()=>setShowLocationEdit(v=>!v)} style={{background:"none",border:"none",color:C.accent,fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>📍 {locationLabel}</button>
          <button onClick={fetchEvents} style={{background:C.accent,border:"none",color:"white",borderRadius:999,padding:"5px 12px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>{loadingEvents?"…":"Search"}</button>
        </div>
      </div>

      {showLocationEdit&&(
        <div style={{background:C.card,borderRadius:14,padding:14,marginBottom:12,border:`1px solid ${C.border}`,display:"flex",gap:8}}>
          <input value={locationInput} onChange={e=>setLocationInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveLocation()} placeholder="Enter zip or city…" style={{flex:1,background:C.inputBg,border:`1px solid ${C.border}`,borderRadius:10,padding:"8px 12px",fontSize:13,color:C.text,outline:"none",fontFamily:"Nunito, sans-serif",fontWeight:600}} />
          <button onClick={saveLocation} style={{background:C.accent,border:"none",color:"white",borderRadius:10,padding:"8px 14px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>Set</button>
          <button onClick={()=>{ setZipOverride(""); navigator.geolocation?.getCurrentPosition(pos=>setLocation({lat:pos.coords.latitude,lng:pos.coords.longitude,source:"device"})); setShowLocationEdit(false); }} style={{background:`${C.accent}18`,border:"none",color:C.accent,borderRadius:10,padding:"8px 10px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>Auto</button>
        </div>
      )}

      {events.length===0&&!loadingEvents&&(
        <div style={{background:`${C.accent}0A`,borderRadius:14,padding:16,border:`1px solid ${C.border}`,textAlign:"center",marginBottom:16}}>
          <div style={{color:C.sub,fontSize:13,fontWeight:600}}>Tap Search to find real events near you 🌍</div>
        </div>
      )}

      {loadingEvents&&(
        <div style={{textAlign:"center",padding:20,color:C.accent,fontSize:13,fontWeight:700}}>Finding events near you…</div>
      )}

      {events.map((e,i)=>(
        <div key={i} style={{background:C.card,borderRadius:14,padding:14,marginBottom:10,border:`1px solid ${C.border}`,display:"flex",gap:12,alignItems:"flex-start"}}>
          <div style={{fontSize:28,width:44,height:44,background:`${C.accent}14`,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{e.emoji}</div>
          <div style={{flex:1}}>
            <div style={{color:C.text,fontSize:14,fontWeight:800}}>{e.name}</div>
            <div style={{color:C.accent,fontSize:11,fontWeight:700,marginTop:2}}>{e.when} · {e.where}</div>
            <div style={{color:C.sub,fontSize:12,fontWeight:600,marginTop:3}}>{e.vibe}</div>
          </div>
          {e.url&&<a href={e.url} target="_blank" rel="noreferrer" style={{color:C.accent,fontSize:11,fontWeight:800,textDecoration:"none",flexShrink:0}}>→</a>}
        </div>
      ))}
    </div>
  );
}

// ─── GIRLS TAB ───────────────────────────────────────────────────
function GirlsTab({C,friends,setFriends,journalEntries,onAddJournalEntry}) {
  const [subTab,setSubTab]=useState("girls"); // girls | journal

  return (
    <div style={{padding:"20px 20px 0"}}>
      <div style={{fontFamily:"Pacifico, cursive",fontSize:22,color:C.text,marginBottom:16}}>
        {subTab==="girls"?"My Girls 💬":"My Journal 📓"}
      </div>

      {/* Sub tabs */}
      <div style={{display:"flex",background:`${C.accent}10`,borderRadius:12,padding:4,marginBottom:20}}>
        {[["girls","💬 Girls"],["journal","📓 Journal"]].map(([id,label])=>(
          <button key={id} onClick={()=>setSubTab(id)} style={{flex:1,background:subTab===id?C.accent:"none",border:"none",borderRadius:9,padding:"9px 0",fontSize:13,fontWeight:800,color:subTab===id?"white":C.sub,cursor:"pointer",fontFamily:"Nunito, sans-serif",transition:"all 0.2s"}}>{label}</button>
        ))}
      </div>

      {subTab==="girls"&&<GirlsList C={C} friends={friends} setFriends={setFriends} />}
      {subTab==="journal"&&<Journal C={C} entries={journalEntries} onAddEntry={onAddJournalEntry} />}
    </div>
  );
}

function GirlsList({C,friends,setFriends}) {
  const [sheet,setSheet]=useState(null);
  const [editName,setEditName]=useState("");
  const [editPhone,setEditPhone]=useState("");
  const [editAvatar,setEditAvatar]=useState(AVATARS[0]);
  const [deleteConfirm,setDeleteConfirm]=useState(null);
  const [composing,setComposing]=useState(false);
  const [selectedRecipients,setSelectedRecipients]=useState([]);
  const [messageBody,setMessageBody]=useState("");

  const openAdd=()=>{ setEditName(""); setEditPhone(""); setEditAvatar(AVATARS[0]); setSheet("add"); };
  const openEdit=f=>{ setEditName(f.name); setEditPhone(f.phone||""); setEditAvatar(f.avatar); setSheet(f); };
  const saveAdd=()=>{ if(!editName.trim()) return; setFriends(fs=>[...fs,{id:`f${Date.now()}`,name:editName.trim(),phone:editPhone.trim(),avatar:editAvatar}]); setSheet(null); };
  const saveEdit=()=>{ if(!editName.trim()) return; setFriends(fs=>fs.map(f=>f.id===sheet.id?{...f,name:editName.trim(),phone:editPhone.trim(),avatar:editAvatar}:f)); setSheet(null); };
  const deleteFriend=id=>{ setFriends(fs=>fs.filter(f=>f.id!==id)); setDeleteConfirm(null); setSheet(null); };
  const openIMessage=(phone,name)=>{ const c=phone.replace(/\D/g,""); if(c) window.open(`sms:${c}`,"_blank"); else alert(`No number saved for ${name}.`); };

  const toggleRecipient=(id)=>{ setSelectedRecipients(r=>r.includes(id)?r.filter(x=>x!==id):[...r,id]); };

  const sendMessage=()=>{
    if(!messageBody.trim()){ alert("Please write a message first."); return; }
    if(selectedRecipients.length===0){ alert("Please select at least one recipient."); return; }
    const numbers=friends.filter(f=>selectedRecipients.includes(f.id)).map(f=>f.phone.replace(/\D/g,"")).filter(Boolean).join(",");
    if(!numbers){ alert("Selected friends have no phone numbers saved."); return; }
    window.open(`sms:${numbers}&body=${encodeURIComponent(messageBody)}`,"_blank");
  };

  if(composing) return (
    <>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
        <button onClick={()=>{ setComposing(false); setSelectedRecipients([]); setMessageBody(""); }} style={{background:"none",border:"none",color:C.accent,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif",padding:0}}>‹ Back</button>
        <div style={{color:C.text,fontSize:16,fontWeight:800}}>New Message</div>
      </div>

      <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Write your message</div>
      <textarea value={messageBody} onChange={e=>setMessageBody(e.target.value)} placeholder="What do you want to say..." rows={4}
        style={{width:"100%",background:C.inputBg,border:`1px solid ${C.border}`,borderRadius:14,padding:12,fontSize:14,color:C.text,outline:"none",fontFamily:"Nunito, sans-serif",fontWeight:600,resize:"none",lineHeight:1.5,boxSizing:"border-box",marginBottom:20}} />

      <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>Send to</div>
      {friends.length===0 && (
        <div style={{color:C.sub,fontSize:13,fontWeight:600,marginBottom:16,textAlign:"center"}}>No girls added yet. Add some friends first.</div>
      )}
      {friends.map(f=>{
        const selected=selectedRecipients.includes(f.id);
        return (
          <div key={f.id} onClick={()=>toggleRecipient(f.id)} style={{background:C.card,border:`1.5px solid ${selected?C.accent:C.border}`,borderRadius:14,padding:"12px 14px",marginBottom:8,display:"flex",alignItems:"center",gap:12,cursor:"pointer",transition:"border-color 0.2s"}}>
            <div style={{width:40,height:40,background:`${C.accent}18`,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{f.avatar}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{color:C.text,fontSize:14,fontWeight:800}}>{f.name}</div>
              <div style={{color:C.sub,fontSize:12,fontWeight:600}}>{f.phone||"No number"}</div>
            </div>
            <div style={{width:24,height:24,borderRadius:"50%",border:`2px solid ${selected?C.accent:C.sub}`,background:selected?C.accent:"none",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.2s"}}>
              {selected&&<span style={{color:"white",fontSize:13,fontWeight:900}}>✓</span>}
            </div>
          </div>
        );
      })}

      <button onClick={sendMessage} style={{width:"100%",background:`linear-gradient(135deg,${C.accent},${C.purple})`,border:"none",color:"white",borderRadius:14,padding:"15px",fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif",marginTop:12}}>
        Send in iMessage 💬
      </button>
    </>
  );

  return (
    <>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{color:C.sub,fontSize:12,fontWeight:600}}>Tap a girl to open iMessage 💬</div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>{ setComposing(true); setSelectedRecipients([]); setMessageBody(""); }} style={{background:`linear-gradient(135deg,${C.purple},${C.accent})`,border:"none",color:"white",borderRadius:999,padding:"7px 14px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>✉️ Message</button>
          <button onClick={openAdd} style={{background:`linear-gradient(135deg,${C.accent},${C.purple})`,border:"none",color:"white",borderRadius:999,padding:"7px 16px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>+ Add</button>
        </div>
      </div>
      {friends.length===0&&(
        <div style={{textAlign:"center",padding:"40px 0",color:C.sub}}>
          <div style={{fontSize:40,marginBottom:10}}>💕</div>
          <div style={{color:C.text,fontSize:16,fontWeight:800,marginBottom:4}}>Add your girls</div>
          <div style={{fontSize:13,fontWeight:600}}>Tap + Add to save a contact</div>
        </div>
      )}
      {friends.map(f=>(
        <div key={f.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:"14px 16px",marginBottom:10,display:"flex",alignItems:"center",gap:14,boxShadow:`0 2px 8px ${C.shadow}`}}>
          <button onClick={()=>openIMessage(f.phone,f.name)} style={{background:"none",border:"none",cursor:"pointer",padding:0,flexShrink:0}}>
            <div style={{width:46,height:46,background:`${C.accent}18`,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{f.avatar}</div>
          </button>
          <button onClick={()=>openIMessage(f.phone,f.name)} style={{flex:1,background:"none",border:"none",cursor:"pointer",textAlign:"left",padding:0,minWidth:0}}>
            <div style={{color:C.text,fontSize:15,fontWeight:800}}>{f.name}</div>
            <div style={{color:C.sub,fontSize:12,fontWeight:600}}>{f.phone||"No number yet"}</div>
          </button>
          <button onClick={()=>openEdit(f)} style={{background:`${C.accent}14`,border:"none",borderRadius:999,padding:"6px 12px",color:C.accent,fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif",flexShrink:0}}>Edit</button>
        </div>
      ))}
      <div style={{background:`${C.accent}0A`,borderRadius:14,padding:14,marginTop:4,border:`1px solid ${C.border}`,textAlign:"center"}}>
        <div style={{color:C.sub,fontSize:12,fontWeight:600,lineHeight:1.6}}>💬 Opens iMessage on your phone.<br/>Your conversations stay in Messages.</div>
      </div>

      {sheet&&(
        <div style={{position:"absolute",inset:0,zIndex:60,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}>
          <div onClick={()=>setSheet(null)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.4)"}} />
          <div style={{position:"relative",background:C.card,borderRadius:"24px 24px 0 0",padding:"10px 0 40px"}}>
            <div style={{width:40,height:5,background:C.border,borderRadius:3,margin:"0 auto 20px"}} />
            <div style={{padding:"0 24px"}}>
              <div style={{color:C.text,fontSize:18,fontWeight:800,marginBottom:20}}>{sheet==="add"?"Add a Girl ✨":`Edit ${sheet.name}`}</div>
              <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>Avatar</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:18}}>
                {AVATARS.map(a=><button key={a} onClick={()=>setEditAvatar(a)} style={{width:42,height:42,borderRadius:"50%",border:`2px solid ${editAvatar===a?C.accent:C.border}`,background:editAvatar===a?`${C.accent}18`:C.inputBg,fontSize:20,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>{a}</button>)}
              </div>
              <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Name</div>
              <input value={editName} onChange={e=>setEditName(e.target.value)} placeholder="Her name…" style={{width:"100%",background:C.inputBg,border:`1px solid ${C.border}`,borderRadius:12,padding:"12px 16px",fontSize:15,color:C.text,outline:"none",fontFamily:"Nunito, sans-serif",fontWeight:700,boxSizing:"border-box",marginBottom:14}} />
              <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Phone Number</div>
              <input value={editPhone} onChange={e=>setEditPhone(e.target.value)} placeholder="+1 555 000 0000" type="tel" style={{width:"100%",background:C.inputBg,border:`1px solid ${C.border}`,borderRadius:12,padding:"12px 16px",fontSize:15,color:C.text,outline:"none",fontFamily:"Nunito, sans-serif",fontWeight:700,boxSizing:"border-box",marginBottom:20}} />
              <button onClick={sheet==="add"?saveAdd:saveEdit} style={{width:"100%",background:`linear-gradient(135deg,${C.accent},${C.purple})`,border:"none",color:"white",borderRadius:14,padding:"14px",fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif",marginBottom:10}}>{sheet==="add"?"Add Girl 💕":"Save Changes ✓"}</button>
              {sheet!=="add"&&(deleteConfirm===sheet.id
                ?<div style={{display:"flex",gap:8}}><button onClick={()=>deleteFriend(sheet.id)} style={{flex:1,background:"#FF4444",border:"none",color:"white",borderRadius:14,padding:"12px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>Yes, remove</button><button onClick={()=>setDeleteConfirm(null)} style={{flex:1,background:C.inputBg,border:`1px solid ${C.border}`,color:C.sub,borderRadius:14,padding:"12px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>Cancel</button></div>
                :<button onClick={()=>setDeleteConfirm(sheet.id)} style={{width:"100%",background:"none",border:`1px solid #FF444444`,color:"#FF4444",borderRadius:14,padding:"12px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>Remove {sheet.name}</button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Parses a stored prompt into a story label + question, when the prompt came
// from the Explore tab. Story prompts are saved as "Title · Part N — Chapter\nQuestion".
function splitStoryPrompt(prompt) {
  if (!prompt) return { label: "", question: "" };
  const nl = prompt.indexOf("\n");
  if (nl < 0) return { label: "", question: prompt };
  const first = prompt.slice(0, nl);
  const rest = prompt.slice(nl + 1).trim();
  if (/^.+ · Part \d+ — .+$/.test(first)) return { label: first, question: rest };
  return { label: "", question: prompt };
}

// ─── JOURNAL ─────────────────────────────────────────────────────
function Journal({C,entries,onAddEntry}) {
  const [writing,setWriting]=useState(false);
  const [mood,setMood]=useState(3);
  const [text,setText]=useState("");
  const [prompt,setPrompt]=useState("");
  const [loadingPrompt,setLoadingPrompt]=useState(false);
  const [saving,setSaving]=useState(false);
  const [saveError,setSaveError]=useState("");
  const [viewEntry,setViewEntry]=useState(null);

  const getPrompt=async()=>{
    setLoadingPrompt(true);
    try {
      const data=await callClaude({model:"claude-sonnet-4-6",max_tokens:100,
        messages:[{role:"user",content:`Give one warm, thoughtful journal prompt for a young woman doing self-reflection. Not generic. Make it feel like it's from a wise older sister. Return only the prompt text, no quotes.`}]
      });
      setPrompt(data.content[0].text.trim());
    } catch { setPrompt(JOURNAL_PROMPTS[Math.floor(Math.random()*JOURNAL_PROMPTS.length)]); }
    setLoadingPrompt(false);
  };

  useEffect(()=>{ if(writing) getPrompt(); },[writing]);

  const save=async()=>{
    if(!text.trim() || saving) return;
    setSaving(true);
    setSaveError("");
    const result = await onAddEntry({ mood, text: text.trim(), prompt });
    setSaving(false);
    if (result?.error) { setSaveError(result.error); return; }
    setText(""); setMood(3); setPrompt(""); setWriting(false);
  };

  if(viewEntry) {
    const { label: viewLabel, question: viewQuestion } = splitStoryPrompt(viewEntry.prompt);
    return (
      <div>
        <button onClick={()=>setViewEntry(null)} style={{background:"none",border:"none",color:C.accent,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif",marginBottom:16,padding:0}}>‹ Back</button>
        <div style={{background:C.card,borderRadius:18,padding:20,border:`1px solid ${C.border}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:28}}>{MOOD_E[viewEntry.mood-1]}</div>
            <div style={{color:C.sub,fontSize:12,fontWeight:600}}>{viewEntry.date} · {viewEntry.time}</div>
          </div>
          {viewLabel && <div style={{display:"inline-block",background:`${C.purple}18`,color:C.purple,fontSize:11,fontWeight:800,letterSpacing:1.2,textTransform:"uppercase",padding:"4px 10px",borderRadius:999,marginBottom:10}}>{viewLabel}</div>}
          {viewQuestion && <div style={{color:C.accent,fontSize:13,fontStyle:"italic",fontWeight:600,marginBottom:12,lineHeight:1.6}}>{viewQuestion}</div>}
          <div style={{color:C.text,fontSize:15,lineHeight:1.75,fontWeight:600}}>{viewEntry.text}</div>
        </div>
      </div>
    );
  }

  if(writing) return (
    <div>
      <button onClick={()=>{setWriting(false);setSaveError("");}} style={{background:"none",border:"none",color:C.accent,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif",marginBottom:16,padding:0}}>‹ Cancel</button>

      {/* Mood */}
      <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>How are you feeling?</div>
      <div style={{display:"flex",gap:10,marginBottom:20}}>
        {MOOD_E.map((e,i)=><button key={i} onClick={()=>setMood(i+1)} style={{flex:1,aspectRatio:"1",background:mood===i+1?`linear-gradient(135deg,${C.accent},${C.purple})`:C.inputBg,border:`1px solid ${mood===i+1?C.accent:C.border}`,borderRadius:12,fontSize:22,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>{e}</button>)}
      </div>

      {/* AI Prompt */}
      <div style={{background:`${C.accent}0F`,borderRadius:14,padding:14,marginBottom:16,border:`1px solid ${C.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
          <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase"}}>Today's prompt</div>
          <button onClick={getPrompt} style={{background:"none",border:"none",color:C.accent,fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>↻ New</button>
        </div>
        {loadingPrompt?<div style={{color:C.accent,fontSize:13,fontWeight:600}}>Finding the right words…</div>
          :<div style={{color:C.text,fontSize:14,fontStyle:"italic",fontWeight:600,lineHeight:1.6}}>{prompt}</div>}
      </div>

      {/* Free write */}
      <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Write freely</div>
      <textarea value={text} onChange={e=>setText(e.target.value)} placeholder="Write whatever needs to come out. This is just for you." rows={8}
        style={{width:"100%",background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:16,fontSize:15,color:C.text,outline:"none",fontFamily:"Georgia, serif",resize:"none",lineHeight:1.7,boxSizing:"border-box",marginBottom:16}} />

      {saveError && (
        <div style={{background:`${C.coral}14`,border:`1px solid ${C.coral}55`,borderRadius:12,padding:"10px 14px",marginBottom:12,color:C.coral,fontSize:13,fontWeight:700,lineHeight:1.5}}>
          Couldn't save: {saveError}
        </div>
      )}
      <button onClick={save} disabled={saving||!text.trim()} style={{width:"100%",background:`linear-gradient(135deg,${C.accent},${C.purple})`,border:"none",color:"white",borderRadius:14,padding:"14px",fontSize:16,fontWeight:800,cursor:saving?"default":"pointer",fontFamily:"Nunito, sans-serif",opacity:(saving||!text.trim())?0.6:1}}>{saving?"Saving…":"Save Entry 🌸"}</button>
    </div>
  );

  return (
    <>
      <button onClick={()=>setWriting(true)} style={{width:"100%",background:`linear-gradient(135deg,${C.accent},${C.purple})`,border:"none",color:"white",borderRadius:16,padding:"16px",fontSize:15,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif",marginBottom:20,display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
        <span style={{fontSize:20}}>📝</span> Write today's entry
      </button>
      {entries.length===0&&(
        <div style={{textAlign:"center",padding:"40px 0",color:C.sub}}>
          <div style={{fontSize:40,marginBottom:10}}>📓</div>
          <div style={{color:C.text,fontSize:16,fontWeight:800,marginBottom:4}}>Your private journal</div>
          <div style={{fontSize:13,fontWeight:600}}>Only you can see this. Write freely.</div>
        </div>
      )}
      {entries.map(e=>{
        const { label } = splitStoryPrompt(e.prompt);
        return (
          <button key={e.id} onClick={()=>setViewEntry(e)} style={{width:"100%",background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:"14px 16px",marginBottom:10,cursor:"pointer",textAlign:"left",boxShadow:`0 2px 8px ${C.shadow}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:20}}>{MOOD_E[e.mood-1]}</span>
                <span style={{color:C.text,fontSize:13,fontWeight:800}}>{e.date}</span>
              </div>
              <span style={{color:C.sub,fontSize:11,fontWeight:600}}>{e.time}</span>
            </div>
            {label && <div style={{color:C.purple,fontSize:10,fontWeight:800,letterSpacing:1.2,textTransform:"uppercase",marginBottom:4}}>{label}</div>}
            <div style={{color:C.sub,fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.text}</div>
          </button>
        );
      })}
    </>
  );
}

// ─── PRIVACY TAB ─────────────────────────────────────────────────
function PrivacyTab({C,dark,setDark,theme,setTheme,quotes,onAddQuote,onDeleteQuote,userEmail,onSignOut}) {
  const [subTab, setSubTab] = useState("privacy");

  return (
    <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,overflowY:"auto",WebkitOverflowScrolling:"touch",background:C.bg,padding:"20px 20px 100px",boxSizing:"border-box"}}>
      <div style={{fontFamily:"Pacifico, cursive",fontSize:22,color:C.text,marginBottom:16}}>
        {subTab==="privacy" ? "Your Space 🔒" : "My Quotes 💬"}
      </div>

      {/* Sub tabs */}
      <div style={{display:"flex",background:`${C.accent}10`,borderRadius:12,padding:4,marginBottom:20}}>
        {[["privacy","🔒 Privacy"],["quotes","✨ Quotes"]].map(([id,label])=>(
          <button key={id} onClick={()=>setSubTab(id)} style={{flex:1,background:subTab===id?C.accent:"none",border:"none",borderRadius:9,padding:"9px 0",fontSize:13,fontWeight:800,color:subTab===id?"white":C.sub,cursor:"pointer",fontFamily:"Nunito, sans-serif",transition:"all 0.2s"}}>{label}</button>
        ))}
      </div>

      {subTab==="privacy" && <PrivacyContent C={C} dark={dark} setDark={setDark} theme={theme} setTheme={setTheme} userEmail={userEmail} onSignOut={onSignOut} />}
      {subTab==="quotes"  && <QuoteManager C={C} quotes={quotes} onAddQuote={onAddQuote} onDeleteQuote={onDeleteQuote} />}
    </div>
  );
}

function PrivacyContent({C,dark,setDark,theme,setTheme,userEmail,onSignOut}) {
  const items=[
    {icon:"🌙",label:"Dark Mode",toggle:true,value:dark,onToggle:()=>setDark(d=>!d)},
    {icon:"📵",label:"No ads. Ever.",toggle:false},
    {icon:"🔒",label:"Your data stays on this device",toggle:false},
    {icon:"🚫",label:"We never sell your data",toggle:false},
    {icon:"🤍",label:"No algorithm, no tracking",toggle:false},
    {icon:"📓",label:"Journal is private — never shared",toggle:false},
  ];
  const selectedMeta = THEME_META.find(t=>t.id===theme);
  return (
    <div>
      {items.slice(0,1).map((item,i)=>(
        <div key={i} style={{background:C.card,borderRadius:14,padding:"14px 16px",marginBottom:10,border:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:20}}>{item.icon}</span>
            <span style={{color:C.text,fontSize:14,fontWeight:700}}>{item.label}</span>
          </div>
          <div onClick={item.onToggle} style={{width:48,height:26,borderRadius:999,background:item.value?C.accent:`${C.sub}44`,cursor:"pointer",position:"relative",transition:"background 0.3s",flexShrink:0}}>
            <div style={{position:"absolute",top:3,left:item.value?25:3,width:20,height:20,borderRadius:"50%",background:"white",transition:"left 0.3s",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}} />
          </div>
        </div>
      ))}

      {/* Theme picker */}
      <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase",marginBottom:10,marginTop:4}}>App Theme</div>
      <div style={{background:C.card,borderRadius:16,padding:"16px",marginBottom:10,border:`1px solid ${C.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          {THEME_META.map(t=>(
            <button key={t.id} onClick={()=>setTheme(t.id)} style={{width:44,height:44,borderRadius:"50%",border:theme===t.id?"3px solid white":"3px solid transparent",background:THEMES[t.id].accent,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:theme===t.id?`0 0 0 2px ${THEMES[t.id].accent}`:"none",transition:"all 0.2s",flexShrink:0,padding:0}}>
              {theme===t.id && <span style={{color:"white",fontSize:18,fontWeight:900,lineHeight:1}}>✓</span>}
            </button>
          ))}
        </div>
        <div style={{color:C.sub,fontSize:12,fontWeight:700,textAlign:"center",marginTop:12}}>{selectedMeta?.emoji} {selectedMeta?.label}</div>
      </div>

      {items.slice(1).map((item,i)=>(
        <div key={i} style={{background:C.card,borderRadius:14,padding:"14px 16px",marginBottom:10,border:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:20}}>{item.icon}</span>
            <span style={{color:C.text,fontSize:14,fontWeight:700}}>{item.label}</span>
          </div>
          <span style={{color:C.accent,fontSize:16}}>✓</span>
        </div>
      ))}
      <div style={{background:`linear-gradient(135deg,${C.accent}14,${C.purple}0A)`,borderRadius:16,padding:20,marginTop:4,marginBottom:20,border:`1px solid ${C.border}`,textAlign:"center"}}>
        <div style={{fontFamily:"Pacifico, cursive",fontSize:18,color:C.accent,marginBottom:6}}>Made with love 🌸</div>
        <div style={{color:C.sub,fontSize:13,fontWeight:600,lineHeight:1.6}}>GirlAF was built for young women who are building themselves. Your GirlAlainaFaith 🌸</div>
      </div>

      {userEmail && (
        <div style={{background:C.card,borderRadius:14,padding:"14px 16px",marginBottom:10,border:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
          <div style={{minWidth:0}}>
            <div style={{color:C.sub,fontSize:10,fontWeight:800,letterSpacing:2,textTransform:"uppercase",marginBottom:2}}>Signed in as</div>
            <div style={{color:C.text,fontSize:13,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{userEmail}</div>
          </div>
          <button onClick={onSignOut} style={{flexShrink:0,background:`${C.accent}14`,border:`1px solid ${C.border}`,borderRadius:999,padding:"8px 16px",fontSize:12,fontWeight:800,color:C.accent,cursor:"pointer",fontFamily:"Nunito, sans-serif"}}>Sign out</button>
        </div>
      )}
    </div>
  );
}

function QuoteManager({C,quotes,onAddQuote,onDeleteQuote}) {
  const [newText, setNewText]         = useState("");
  const [newAuthor, setNewAuthor]     = useState("");
  const [newEra, setNewEra]           = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showQuotes, setShowQuotes]   = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);

  const handleAdd = () => {
    if (!newText.trim() || !newAuthor.trim()) return;
    onAddQuote({text: newText.trim(), author: newAuthor.trim(), era: newEra.trim()});
    setNewText(""); setNewAuthor(""); setNewEra("");
  };

  const handleDelete = (idx) => {
    if (deleteConfirm === idx) { onDeleteQuote(idx); setDeleteConfirm(null); setExpandedIdx(null); }
    else setDeleteConfirm(idx);
  };

  const inputStyle = {width:"100%",background:C.inputBg,border:`1px solid ${C.border}`,borderRadius:12,padding:"10px 14px",fontSize:14,color:C.text,outline:"none",fontFamily:"Nunito, sans-serif",fontWeight:600,boxSizing:"border-box"};

  return (
    <div>
      <div style={{background:C.card,borderRadius:16,padding:16,border:`1px solid ${C.border}`,marginBottom:16}}>
        <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase",marginBottom:12}}>Add a Quote</div>
        <textarea
          value={newText} onChange={e=>setNewText(e.target.value)}
          placeholder="Quote text…" rows={3}
          style={{...inputStyle, resize:"none", lineHeight:1.5, marginBottom:10}}
        />
        <input
          value={newAuthor} onChange={e=>setNewAuthor(e.target.value)}
          placeholder="Author" style={{...inputStyle, marginBottom:10}}
        />
        <input
          value={newEra} onChange={e=>setNewEra(e.target.value)}
          placeholder="Era (optional)" style={{...inputStyle, marginBottom:14}}
        />
        <button
          onClick={handleAdd}
          disabled={!newText.trim() || !newAuthor.trim()}
          style={{width:"100%",background:newText.trim()&&newAuthor.trim()?`linear-gradient(135deg,${C.accent},${C.purple})`:C.fade,border:"none",color:"white",borderRadius:12,padding:"12px",fontSize:15,fontWeight:800,cursor:newText.trim()&&newAuthor.trim()?"pointer":"default",fontFamily:"Nunito, sans-serif"}}
        >Add Quote</button>
      </div>

      {/* Collapsible quotes list */}
      <button
        onClick={()=>{ setShowQuotes(s=>!s); setExpandedIdx(null); setDeleteConfirm(null); }}
        style={{width:"100%",background:`${C.accent}14`,border:"none",borderRadius:12,padding:"12px 16px",color:C.accent,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"Nunito, sans-serif",textAlign:"left",marginBottom:showQuotes?0:20}}
      >My Quotes ({quotes.length}) {showQuotes?"▲":"▼"}</button>

      {showQuotes && (
        <div style={{background:C.card,borderRadius:"0 0 16px 16px",border:`1px solid ${C.border}`,borderTop:"none",overflow:"hidden",marginBottom:20}}>
          {quotes.length === 0 && (
            <div style={{padding:20,color:C.sub,fontSize:13,fontWeight:600,textAlign:"center"}}>No quotes yet.</div>
          )}
          {quotes.map((q, i) => (
            <div key={i} style={{borderBottom:`1px solid ${C.border}44`}}>
              <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px"}}>
                <div
                  onClick={()=>setExpandedIdx(expandedIdx===i?null:i)}
                  style={{flex:1,color:C.accent,fontSize:13,fontWeight:800,cursor:"pointer"}}
                >
                  {q.author}{q.era ? <span style={{color:C.sub,fontWeight:600}}> · {q.era}</span> : ""}
                </div>
                <button
                  onClick={()=>handleDelete(i)}
                  style={{flexShrink:0,background:deleteConfirm===i?"#FF4444":"none",border:"none",borderRadius:8,padding:"4px 8px",fontSize:14,fontWeight:800,color:deleteConfirm===i?"white":C.accent,cursor:"pointer",fontFamily:"Nunito, sans-serif",lineHeight:1}}
                >{deleteConfirm===i?"✓":"✕"}</button>
              </div>
              {expandedIdx===i && (
                <div style={{padding:"0 14px 12px",color:C.text,fontSize:13,fontWeight:600,fontStyle:"italic",lineHeight:1.6}}>
                  "{q.text}"
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
